import React from "react";
import EligibilityRulesManagerPage from "./EligibilityRulesManagerPage";
import TranslationAdminPage from "./TranslationAdminPage";
import AddonRulesManagerPage from "./AddonRulesManagerPage";
import BeneficiaryInsureeDetailsPage from "./BeneficiaryInsureeDetailsPage";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import { Add, DeleteOutline, EditOutlined, Tune } from "@material-ui/icons";

const RIGHT_BENEFIT_PLAN_SEARCH = 160001;

const getBackendBaseUrl = () => {
  const configured = (window.localStorage.getItem("openimis_backend_base_url") || "").trim();
  if (configured) return configured.endsWith("/") ? configured.slice(0, -1) : configured;
  if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
    return `${window.location.protocol}//${window.location.hostname}:3000`;
  }
  return `${window.location.protocol}//${window.location.host}`;
};

const unique = (items) => Array.from(new Set(items.filter(Boolean)));

const asStringMap = (value) => {
  if (!value || typeof value !== "object") return {};
  return value;
};

const getGraphqlHeaders = () => {
  const csrfToken = localStorage.getItem("csrfToken");
  return {
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
    ...(csrfToken ? { "X-CSRFToken": csrfToken } : {}),
  };
};

const extractPayloadMap = (body) => {
  const data = body?.data;
  const result = body?.result;
  if (data && typeof data === "object" && !Array.isArray(data)) return data;
  if (result && typeof result === "object" && !Array.isArray(result)) return result;
  return body || {};
};

const extractMapList = (root, keys) => {
  for (const key of keys) {
    const raw = root?.[key];
    if (Array.isArray(raw)) return raw.map((item) => asStringMap(item));
  }
  return [];
};

const buildApiBaseCandidates = () => {
  const configured = (window.localStorage.getItem("openimis_backend_base_url") || "").trim();
  const normalizedConfigured = configured ? configured.replace(/\/$/, "") : "";
  const sameOrigin = `${window.location.protocol}//${window.location.host}`;
  const inferredDefault = getBackendBaseUrl();
  return unique([normalizedConfigured, sameOrigin, inferredDefault]);
};

const buildApiRoots = (baseUrl) => {
  const raw = String(baseUrl || "").trim().replace(/\/$/, "");
  if (!raw) return [];

  const baseCandidates = [raw];
  if (raw.endsWith("/api")) baseCandidates.push(raw.slice(0, -4));
  if (raw.endsWith("/front")) baseCandidates.push(raw.slice(0, -6));

  try {
    const parsed = new URL(raw);
    baseCandidates.push(`${parsed.protocol}//${parsed.host}`);
  } catch (error) {
    // Ignore malformed URL and keep raw candidates only.
  }

  const uniqueBases = unique(baseCandidates.map((x) => String(x || "").replace(/\/$/, "")));
  const roots = [];
  for (const base of uniqueBases) {
    roots.push(base.endsWith("/api") ? base : `${base}/api`);
  }
  return unique(roots.map((x) => x.replace(/\/$/, "")));
};

const buildSocialRuleEndpoints = (baseCandidates) => {
  const roots = unique(baseCandidates.flatMap((base) => buildApiRoots(base)));
  return {
    rules: roots.flatMap((root) => [
      `${root}/sync/social-assessment-rules/`,
      `${root}/sync/eligibility-rules/`,
    ]),
    ruleDetail: (uuid) => roots.flatMap((root) => [
      `${root}/sync/social-assessment-rules/${uuid}/`,
      `${root}/sync/eligibility-rules/${uuid}/`,
    ]),
  };
};

const FIELD_TYPES = ["text", "number", "boolean", "select", "date"];

const SOCIAL_CONDITION_FIELDS = [
  { key: "beneficiary.income", label: "Monthly Income" },
  { key: "beneficiary.household_size", label: "Household Size" },
  { key: "beneficiary.is_previous_beneficiary", label: "Previously Registered" },
  { key: "beneficiary.idp_status", label: "IDP Status" },
  { key: "insuree.age", label: "Age (years)" },
  { key: "insuree.gender", label: "Gender" },
];
const CONDITION_OPERATORS = [
  { key: "eq", label: "= equals" },
  { key: "ne", label: "≠ not equal" },
  { key: "lt", label: "< less than" },
  { key: "lte", label: "≤ at most" },
  { key: "gt", label: "> greater than" },
  { key: "gte", label: "≥ at least" },
  { key: "contains", label: "contains" },
  { key: "in", label: "in list" },
  { key: "not_in", label: "not in list" },
];
const OPERATORS_BY_TYPE = {
  number: ["eq", "ne", "lt", "lte", "gt", "gte"],
  date: ["eq", "ne", "lt", "lte", "gt", "gte"],
  boolean: ["eq", "ne"],
  select: ["eq", "ne"],
  text: ["eq", "ne", "contains"],
};
const SOCIAL_UI_FIELDS_STORAGE_KEY = "openimis_social_ui_builder_fields";
const SOCIAL_UI_SECTIONS_STORAGE_KEY = "openimis_social_ui_builder_sections";
const CUSTOM_SECTION_VALUE = "__custom__";

const normalizeSectionTitle = (rawSection) => {
  const raw = String(rawSection || "").trim();
  return raw || "General";
};

const canonicalSectionKey = (rawSection) => {
  const raw = normalizeSectionTitle(rawSection);

  const normalized = raw
    .toLowerCase()
    .replace(/[()]/g, "")
    .replace(/\s+/g, " ")
    .trim();

  const aliases = {
    "section 3.1 daq adult": "section-3-1-daq-adult",
    "daq adult": "section-3-1-daq-adult",

    "section 3.2 daq >2 years": "section-3-2-daq-gt2-years",
    "section 3.2 daq 2+ years": "section-3-2-daq-gt2-years",
    "section 3.2 daq 2 years": "section-3-2-daq-gt2-years",
    "daq child 2+": "section-3-2-daq-gt2-years",

    "section 3.3 daq 2-5 years": "section-3-3-daq-2-5-years",
    "section 3.3 daq 2 to 5 years": "section-3-3-daq-2-5-years",
    "daq child 2-5 years": "section-3-3-daq-2-5-years",

    "section 4 poverty assessment": "section-4-poverty-assessment",
    "poverty assessment": "section-4-poverty-assessment",

    "idp assessment": "idp-assessment",
  };

  return aliases[normalized] || normalized;
};

const normalizeSectionDefinition = (raw = {}) => ({
  title: normalizeSectionTitle(raw.title || raw.section || raw.name || "General"),
  description: String(raw.description || "").trim(),
  order: Number.isFinite(Number(raw.order)) ? Math.max(1, Math.trunc(Number(raw.order))) : 999,
  minAge: normalizeOptionalInt(raw.minAge ?? raw.min_age ?? raw?.visibility?.age_min),
  maxAge: normalizeOptionalInt(raw.maxAge ?? raw.max_age ?? raw?.visibility?.age_max),
  aliases: unique(
    (Array.isArray(raw.aliases) ? raw.aliases : [])
      .map((alias) => normalizeSectionTitle(alias))
      .filter(Boolean),
  ),
  conditions: Array.isArray(raw.conditions) ? raw.conditions : [],
});

const toSafeInt = (value, fallback) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(0, Math.trunc(parsed));
};

const normalizeFieldType = (rawType) => {
  const type = String(rawType || "text").trim().toLowerCase();
  if (type === "choice" || type === "dropdown") return "select";
  return FIELD_TYPES.includes(type) ? type : "text";
};

const normalizeRuleOrder = (value, fallback = null) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return Math.max(1, Math.trunc(num));
};

const parseOptions = (raw) => {
  if (Array.isArray(raw)) {
    return unique(
      raw
        .map((item) => {
          if (item && typeof item === "object") {
            const value = String(item.value || "").trim();
            const label = String(item.label || "").trim();
            return value || label;
          }
          return String(item || "").trim();
        })
        .filter(Boolean),
    );
  }
  if (typeof raw === "string") {
    return unique(
      raw
        .split(/[\n,]/)
        .map((item) => item.trim())
        .filter(Boolean),
    );
  }
  return [];
};

const normalizeField = (raw = {}) => ({
  label: String(raw.label || raw.name || "").trim(),
  labelMy: String(raw.label_my || raw.labelMy || "").trim(),
  key: String(raw.key || raw.code || raw.field || "").trim(),
  section: normalizeSectionTitle(raw.section || raw.group || "General"),
  subsection: String(raw.subsection || "").trim(),
  order: normalizeRuleOrder(raw.order, ""),
  type: normalizeFieldType(raw.type),
  minAge: raw.minAge ?? raw.min_age ?? raw?.visibility?.age_min ?? "",
  maxAge: raw.maxAge ?? raw.max_age ?? raw?.visibility?.age_max ?? "",
  helpText: String(raw.helpText || raw.description || "").trim(),
  required: Boolean(raw.required),
  options: parseOptions(raw.options || raw.choices || raw.values || []),
  // Only meaningful for type "select" (choice_buttons on the tablet) — lets
  // the beneficiary pick more than one option instead of just one.
  multiple: Boolean(raw.multiple),
});

const readStoredFields = () => {
  try {
    const stored = window.localStorage.getItem(SOCIAL_UI_FIELDS_STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeField).filter((f) => f.label && f.key);
  } catch (error) {
    return [];
  }
};

const writeStoredFields = (fields) => {
  window.localStorage.setItem(SOCIAL_UI_FIELDS_STORAGE_KEY, JSON.stringify(fields));
};

const readStoredSections = () => {
  try {
    const stored = window.localStorage.getItem(SOCIAL_UI_SECTIONS_STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeSectionDefinition);
  } catch (error) {
    return [];
  }
};

const writeStoredSections = (sections) => {
  window.localStorage.setItem(SOCIAL_UI_SECTIONS_STORAGE_KEY, JSON.stringify(sections));
};

const parseServerSchemaToFields = (payload) => {
  if (!payload) return [];
  if (payload && typeof payload === "object" && payload.schema && typeof payload.schema === "object") {
    return parseServerSchemaToFields(payload.schema);
  }
  if (Array.isArray(payload)) return payload.map(normalizeField).filter((f) => f.label && f.key);
  if (payload && typeof payload === "object" && payload.type === "object" && payload.properties) {
    const required = new Set(Array.isArray(payload.required) ? payload.required : []);
    return Object.entries(payload.properties)
      .map(([key, prop]) => {
        const property = prop && typeof prop === "object" ? prop : {};
        const ui = property.x_ui && typeof property.x_ui === "object" ? property.x_ui : {};
        const inferredType = ui.widget || (Array.isArray(property.enum) ? "select" : property.format === "date" ? "date" : property.type);
        return normalizeField({
          key,
          label: property.title || property.label || key,
          section: ui.section || "General",
          subsection: ui.subsection || "",
          helpText: ui.help_text || "",
          min_age: ui.min_age ?? "",
          max_age: ui.max_age ?? "",
          type: inferredType,
          required: required.has(key),
          options: property.enum || [],
        });
      })
      .filter((f) => f.label && f.key);
  }
  if (Array.isArray(payload.fields)) {
    const sectionFromScreen = String(payload?.screen?.name || payload?.section || "").trim();
    const minAgeFromVisibility = payload?.visibility?.age_min;
    const maxAgeFromVisibility = payload?.visibility?.age_max;
    return payload.fields
      .map((field) => normalizeField(
        sectionFromScreen
          ? {
              ...field,
              section: field?.section || sectionFromScreen,
              min_age: field?.min_age ?? minAgeFromVisibility,
              max_age: field?.max_age ?? maxAgeFromVisibility,
            }
          : field,
      ))
      .filter((f) => f.label && f.key);
  }
  if (Array.isArray(payload.steps)) {
    return payload.steps.flatMap((step) => {
      const section = step?.title || step?.section || "General";
      const fields = Array.isArray(step?.fields) ? step.fields : [];
      return fields
        .map((field) => normalizeField({ ...field, section }))
        .filter((f) => f.label && f.key);
    });
  }
  return [];
};

const parseVersionNumber = (value, fallback = 1) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return Math.max(1, Math.trunc(num));
};

const buildVersionedSchemaEnvelope = (nextSchema, previousRaw) => {
  const now = new Date().toISOString();
  const previous = previousRaw && typeof previousRaw === "object" ? previousRaw : null;

  const prevEnvelope = previous && previous.schema && typeof previous.schema === "object"
    ? previous
    : {
        schema_version: parseVersionNumber(previous?.schema_version || previous?.version, 1),
        version: parseVersionNumber(previous?.schema_version || previous?.version, 1),
        saved_at: now,
        schema: previous && typeof previous === "object" ? previous : { type: "object", properties: {} },
        previous_versions: [],
      };

  const prevVersion = parseVersionNumber(prevEnvelope?.schema_version || prevEnvelope?.version, 1);
  const prevSchema = prevEnvelope?.schema && typeof prevEnvelope.schema === "object"
    ? prevEnvelope.schema
    : { type: "object", properties: {} };
  const prevSavedAt = String(prevEnvelope?.saved_at || now);

  const historical = Array.isArray(prevEnvelope?.previous_versions)
    ? prevEnvelope.previous_versions.filter((item) => item && typeof item === "object")
    : [];

  const newVersion = prevVersion + 1;
  return {
    schema_version: newVersion,
    version: newVersion,
    saved_at: now,
    schema: nextSchema,
    previous_versions: [
      ...historical,
      {
        version: prevVersion,
        saved_at: prevSavedAt,
        schema: prevSchema,
      },
    ].slice(-20),
  };
};

const parseGraphQlPlansToFields = (payload) => {
  const plansA = payload?.data?.benefitPlan?.edges || [];
  const plansB = payload?.data?.benefitPlans?.edges || [];
  const plans = plansA.length ? plansA : plansB;
  return plans.flatMap((edge) => {
    const schema = edge?.node?.beneficiaryDataSchema;
    if (!schema) return [];
    if (typeof schema === "string") {
      try {
        return parseServerSchemaToFields(JSON.parse(schema));
      } catch (error) {
        return [];
      }
    }
    return parseServerSchemaToFields(schema);
  });
};

const parseGraphQlPlansMeta = (payload) => {
  const plansA = payload?.data?.benefitPlan?.edges || [];
  const plansB = payload?.data?.benefitPlans?.edges || [];
  const plans = plansA.length ? plansA : plansB;

  const planDetails = plans
    .map((edge) => {
      const node = edge?.node || {};
      const id = String(node?.uuid || node?.id || "").trim();
      if (!id) return null;
      return {
        id,
        code: node?.code,
        name: node?.name,
        type: node?.type,
        dateValidFrom: normalizeGraphQlDate(node?.dateValidFrom),
        dateValidTo: normalizeGraphQlDate(node?.dateValidTo),
      };
    })
    .filter(Boolean);

  const fields = plans.flatMap((edge) => {
    const schema = edge?.node?.beneficiaryDataSchema;
    if (!schema) return [];
    if (typeof schema === "string") {
      try {
        return parseServerSchemaToFields(JSON.parse(schema));
      } catch (error) {
        return [];
      }
    }
    return parseServerSchemaToFields(schema);
  });

  return { fields, planDetails };
};

const extractSocialRulesFromPayload = (payload) => {
  const root = payload && typeof payload === "object" ? payload : {};
  const candidates = [
    root?.rules,
    root?.data?.rules,
    root?.result?.rules,
    root?.data,
    root?.result,
    root,
  ];

  const list = candidates.find((item) => Array.isArray(item));
  if (!Array.isArray(list)) return [];
  return list.filter((item) => item && typeof item === "object");
};

const parseSocialRulesPayloadToFields = (payload) => {
  const list = extractSocialRulesFromPayload(payload);
  if (!list.length) return [];

  const latestBySection = new Map();
  const sorted = [...list].sort((a, b) => {
    const aUi = a?.ui_schema && typeof a.ui_schema === "object" ? a.ui_schema : {};
    const bUi = b?.ui_schema && typeof b.ui_schema === "object" ? b.ui_schema : {};
    const av = parseVersionNumber(a?.schema_version || aUi?.schema_version || aUi?.version, 1);
    const bv = parseVersionNumber(b?.schema_version || bUi?.schema_version || bUi?.version, 1);
    if (av !== bv) return bv - av;
    const at = Date.parse(String(a?.updated_at || a?.created_at || aUi?.saved_at || ""));
    const bt = Date.parse(String(b?.updated_at || b?.created_at || bUi?.saved_at || ""));
    if (Number.isFinite(at) && Number.isFinite(bt) && at !== bt) return bt - at;
    const aid = Number(a?.id || 0);
    const bid = Number(b?.id || 0);
    return bid - aid;
  });

  for (const item of sorted) {
    const uiSchema = item?.ui_schema && typeof item.ui_schema === "object" ? item.ui_schema : {};
    const screen = uiSchema?.screen && typeof uiSchema.screen === "object" ? uiSchema.screen : {};
    const key = canonicalSectionKey(screen?.name || item?.name || "General");
    if (!latestBySection.has(key)) {
      latestBySection.set(key, item);
    }
  }

  const allFields = [];
  for (const item of latestBySection.values()) {
    if (!item || typeof item !== "object") continue;
    const uiSchema = item.ui_schema && typeof item.ui_schema === "object" ? item.ui_schema : null;
    if (!uiSchema) continue;
    const parsed = parseServerSchemaToFields(uiSchema);
    if (parsed.length) allFields.push(...parsed);
  }
  return allFields;
};

const parseSocialRulesPayloadToSections = (payload) => {
  const list = extractSocialRulesFromPayload(payload);
  if (!list.length) return [];

  const latestBySection = new Map();
  const sorted = [...list].sort((a, b) => {
    const aUi = a?.ui_schema && typeof a.ui_schema === "object" ? a.ui_schema : {};
    const bUi = b?.ui_schema && typeof b.ui_schema === "object" ? b.ui_schema : {};
    const av = parseVersionNumber(a?.schema_version || aUi?.schema_version || aUi?.version, 1);
    const bv = parseVersionNumber(b?.schema_version || bUi?.schema_version || bUi?.version, 1);
    if (av !== bv) return bv - av;
    const at = Date.parse(String(a?.updated_at || a?.created_at || aUi?.saved_at || ""));
    const bt = Date.parse(String(b?.updated_at || b?.created_at || bUi?.saved_at || ""));
    if (Number.isFinite(at) && Number.isFinite(bt) && at !== bt) return bt - at;
    const aid = Number(a?.id || 0);
    const bid = Number(b?.id || 0);
    return bid - aid;
  });

  for (const item of sorted) {
    const uiSchema = item?.ui_schema && typeof item.ui_schema === "object" ? item.ui_schema : {};
    const screen = uiSchema?.screen && typeof uiSchema.screen === "object" ? uiSchema.screen : {};
    const key = canonicalSectionKey(screen?.name || item?.name || "General");
    if (!latestBySection.has(key)) {
      latestBySection.set(key, normalizeSectionDefinition({
        title: screen?.name || item?.name || "General",
        description: screen?.description || "",
        order: normalizeRuleOrder(screen?.order, 999),
        min_age: uiSchema?.visibility?.age_min ?? item?.min_age,
        max_age: uiSchema?.visibility?.age_max ?? item?.max_age,
        conditions: Array.isArray(item?.conditions) ? item.conditions : [],
      }));
    }
  }

  return Array.from(latestBySection.values())
    .sort((a, b) => (a.order - b.order) || a.title.localeCompare(b.title));
};

const normalizeGraphQlDate = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const matched = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  return matched ? matched[1] : raw;
};

const mapToJsonSchemaType = (fieldType) => {
  switch (fieldType) {
    case "number":
      return "number";
    case "boolean":
      return "boolean";
    case "date":
      return "string";
    case "select":
      return "string";
    case "text":
    default:
      return "string";
  }
};

const normalizeOptionalInt = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const num = Number(raw);
  if (!Number.isFinite(num)) return null;
  return Math.trunc(num);
};

const isUuidLike = (value) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(value || "").trim());

const dedupeFields = (fields) => {
  const seen = new Set();
  return fields.filter((field) => {
    const dedupeKey = `${field.key}::${field.section}`;
    if (seen.has(dedupeKey)) return false;
    seen.add(dedupeKey);
    return true;
  });
};

const groupFieldsBySection = (fields) => {
  const grouped = new Map();
  fields.forEach((field, index) => {
    const section = normalizeSectionTitle(field?.section || "General");
    if (!grouped.has(section)) grouped.set(section, []);
    grouped.get(section).push({ field, index });
  });
  return Array.from(grouped.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([section, items]) => ({ section, items }));
};

const normalizeOptionsForCompare = (raw) => unique(parseOptions(raw || [])).sort((a, b) => a.localeCompare(b));

const buildSectionSignature = (sectionName, sectionFields) => {
  const normalized = (sectionFields || [])
    .map((field) => ({
      section: canonicalSectionKey(sectionName || "General"),
      key: String(field?.key || "").trim(),
      label: String(field?.label || "").trim(),
      type: normalizeFieldType(field?.type),
      required: Boolean(field?.required),
      subsection: String(field?.subsection || "").trim(),
      helpText: String(field?.helpText || field?.helper_text || "").trim(),
      minAge: normalizeOptionalInt(field?.minAge ?? field?.min_age),
      maxAge: normalizeOptionalInt(field?.maxAge ?? field?.max_age),
      order: normalizeRuleOrder(field?.order, 9999),
      options: normalizeOptionsForCompare(field?.options || field?.choices || []),
    }))
    .filter((field) => field.key)
    .sort((a, b) => {
      const keyCmp = a.key.localeCompare(b.key);
      if (keyCmp !== 0) return keyCmp;
      return a.label.localeCompare(b.label);
    });

  return JSON.stringify(normalized);
};

const buildSectionMetaSignature = (sectionMeta) => {
  const meta = normalizeSectionDefinition(sectionMeta || {});
  return JSON.stringify({
    title: canonicalSectionKey(meta.title),
    description: String(meta.description || "").trim(),
    order: normalizeRuleOrder(meta.order, 999),
    minAge: normalizeOptionalInt(meta.minAge),
    maxAge: normalizeOptionalInt(meta.maxAge),
    conditions: (Array.isArray(meta.conditions) ? meta.conditions : []).map(
      (c) => ({ field: c.field || "", operator: c.operator || "", value: String(c.value ?? ""), formula: c.formula || "", connector: c.connector || "" }),
    ),
  });
};

const SocialUiBuilderPage = () => {
  const [form, setForm] = React.useState({
    label: "",
    key: "",
    section: "",
    subsection: "",
    order: "",
    type: "text",
    helpText: "",
    required: false,
    options: [],
  });
  const [optionDraft, setOptionDraft] = React.useState("");
  const [fields, setFields] = React.useState(() => readStoredFields());
  const [status, setStatus] = React.useState("Loading existing data...");
  const [editIndex, setEditIndex] = React.useState(null);
  const [useCustomSection, setUseCustomSection] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [serverBenefitPlans, setServerBenefitPlans] = React.useState([]);
  const [expandedSections, setExpandedSections] = React.useState({});
  const [sections, setSections] = React.useState(() => readStoredSections());
  const [sectionForm, setSectionForm] = React.useState({
    originalTitle: "",
    title: "",
    description: "",
    order: "",
    minAge: "",
    maxAge: "",
  });

  const [inlineSection, setInlineSection] = React.useState(null);

  const update = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));
  const sectionOptions = React.useMemo(() => {
    const normalized = sections
      .map((section) => normalizeSectionDefinition(section))
      .sort((a, b) => (a.order - b.order) || a.title.localeCompare(b.title));
    return unique(normalized.map((section) => section.title));
  }, [sections]);
  const sectionMetaByTitle = React.useMemo(() => {
    const map = new Map();
    sections.forEach((section) => {
      const normalized = normalizeSectionDefinition(section);
      map.set(normalized.title, normalized);
    });
    return map;
  }, [sections]);
  const groupedConfiguredFields = React.useMemo(() => groupFieldsBySection(fields), [fields]);

  const existingChoiceOptions = React.useMemo(() => {
    const currentSection = String(form.section || "").trim();
    const fromSection = fields
      .filter((field) => field.type === "select" && String(field.section || "").trim() === currentSection)
      .flatMap((field) => parseOptions(field.options || []));
    const fallbackAll = fields
      .filter((field) => field.type === "select")
      .flatMap((field) => parseOptions(field.options || []));

    const source = fromSection.length ? fromSection : fallbackAll;
    return unique(source).sort((a, b) => a.localeCompare(b));
  }, [fields, form.section]);

  const addOption = (value) => {
    const cleaned = String(value || "").trim();
    if (!cleaned) return;
    setForm((prev) => ({
      ...prev,
      options: unique([...(prev.options || []), cleaned]),
    }));
    setOptionDraft("");
  };

  const removeOption = (option) => {
    setForm((prev) => ({
      ...prev,
      options: (prev.options || []).filter((item) => item !== option),
    }));
  };

  React.useEffect(() => {
    writeStoredFields(fields);
  }, [fields]);

  React.useEffect(() => {
    writeStoredSections(sections);
  }, [sections]);

  React.useEffect(() => {
    const fieldSections = unique(fields.map((field) => normalizeSectionTitle(field?.section || "General")));
    if (!fieldSections.length) return;

    setSections((prev) => {
      const existing = new Set(prev.map((section) => normalizeSectionTitle(section?.title)));
      const missing = fieldSections.filter((title) => !existing.has(title));
      if (!missing.length) return prev;
      const maxOrder = prev.reduce((max, section) => Math.max(max, normalizeSectionDefinition(section).order), 0);
      return [
        ...prev,
        ...missing.map((title, idx) => normalizeSectionDefinition({ title, order: maxOrder + idx + 1 })),
      ];
    });
  }, [fields]);

  React.useEffect(() => {
    let isMounted = true;

    const loadFromServer = async () => {
      const debugTrace = [];
      const pushTrace = (entry) => {
        const withTs = {
          ...entry,
          at: new Date().toISOString(),
        };
        debugTrace.push(withTs);
        return withTs;
      };

      const flushTrace = (finalStatus) => {
        if (!debugTrace.length) return;
        console.groupCollapsed("[SocialUIBuilder] Data load trace");
        console.log("finalStatus", finalStatus);
        debugTrace.forEach((entry, idx) => {
          console.log(`#${idx + 1}`, entry);
        });
        console.groupEnd();
      };

      const apiBases = buildApiBaseCandidates();
      const restCandidates = apiBases.map((base) => `${base}/api/sync/assessment-ui-schema/`);
      const socialRuleCandidates = buildSocialRuleEndpoints(apiBases).rules;
      pushTrace({ stage: "start", apiBases, restCandidates });

      for (const url of socialRuleCandidates) {
        try {
          pushTrace({ stage: "social_rules_attempt", url });
          const res = await fetch(url, { credentials: "include" });
          if (res.status === 401 || res.status === 403) {
            pushTrace({ stage: "social_rules_auth_blocked", url, status: res.status });
            continue;
          }
          if (!res.ok) {
            pushTrace({ stage: "social_rules_http_not_ok", url, status: res.status });
            continue;
          }

          const payload = await res.json();
          const incoming = parseSocialRulesPayloadToFields(payload);
          const incomingSections = parseSocialRulesPayloadToSections(payload);
          pushTrace({ stage: "social_rules_payload_received", url, incomingCount: incoming.length });
          if (!incoming.length) continue;

          if (isMounted) {
            setFields(dedupeFields(incoming));
            if (incomingSections.length) {
              setSections(incomingSections);
            }
            setStatus(`Loaded ${incoming.length} existing fields from social assessment rules.`);
          }
          flushTrace(`Loaded ${incoming.length} existing fields from social assessment rules.`);
          return;
        } catch (error) {
          pushTrace({ stage: "social_rules_fetch_error", url, error: String(error) });
        }
      }

      try {
        pushTrace({
          stage: "graphql_primary_load",
          reason: "Save uses benefit plan schema, so reload prioritizes same source",
        });
        const gqlCandidates = apiBases.map((base) => `${base}/api/graphql`);
        const gqlQueries = [
          {
            name: "SocialUiExistingSchemas",
            query: `
              query SocialUiExistingSchemas {
                benefitPlan(first: 30) {
                  edges {
                    node {
                      id
                      uuid
                      code
                      name
                      type
                      dateValidFrom
                      dateValidTo
                      beneficiaryDataSchema
                    }
                  }
                }
              }
            `,
          },
        ];
        let fromPlans = [];
        let fromPlanDetails = [];

        for (const gqlUrl of gqlCandidates) {
          for (const gqlQuery of gqlQueries) {
            pushTrace({
              stage: "gql_attempt",
              gqlUrl,
              queryName: gqlQuery.name,
            });
            const gqlRes = await fetch(gqlUrl, {
              method: "POST",
              credentials: "include",
              headers: getGraphqlHeaders(),
              body: JSON.stringify({ query: gqlQuery.query }),
            });

            if (gqlRes.status === 401 || gqlRes.status === 403) {
              pushTrace({ stage: "gql_auth_blocked", gqlUrl, status: gqlRes.status });
              if (isMounted) setStatus("Server data is protected. Using local saved data for now.");
              continue;
            }
            if (!gqlRes.ok) {
              pushTrace({ stage: "gql_http_not_ok", gqlUrl, status: gqlRes.status });
              continue;
            }

            const gqlPayload = await gqlRes.json();
            if (Array.isArray(gqlPayload?.errors) && gqlPayload.errors.length) {
              pushTrace({
                stage: "gql_payload_errors",
                gqlUrl,
                queryName: gqlQuery.name,
                errors: gqlPayload.errors.map((e) => e?.message).filter(Boolean),
              });
              continue;
            }
            const parsedMeta = parseGraphQlPlansMeta(gqlPayload);
            const parsed = parsedMeta.fields;
            pushTrace({
              stage: "gql_payload_received",
              gqlUrl,
              queryName: gqlQuery.name,
              parsedCount: parsed.length,
              hasBenefitPlan: Boolean(gqlPayload?.data?.benefitPlan),
              hasBenefitPlans: Boolean(gqlPayload?.data?.benefitPlans),
            });
            if (parsed.length) {
              fromPlans = parsed;
              fromPlanDetails = parsedMeta.planDetails;
              pushTrace({ stage: "gql_parsed_non_empty", gqlUrl, parsedCount: parsed.length });
              break;
            }
            pushTrace({ stage: "gql_parsed_empty", gqlUrl });
          }
          if (fromPlans.length) break;
        }

        if (isMounted) {
          if (fromPlans.length) {
            setFields(dedupeFields(fromPlans));
            setServerBenefitPlans(fromPlanDetails);
            setStatus(`Loaded ${fromPlans.length} existing fields from saved programs.`);
            flushTrace(`Loaded ${fromPlans.length} existing fields from saved programs.`);
            return;
          } else {
            pushTrace({
              stage: "graphql_empty",
              reason: "No beneficiaryDataSchema values found in benefit plans",
            });
          }
        }
      } catch (error) {
        pushTrace({ stage: "gql_fetch_error", error: String(error) });
      }

      for (const url of restCandidates) {
        try {
          pushTrace({ stage: "rest_attempt", url });
          const res = await fetch(url, { credentials: "include" });
          if (res.status === 401 || res.status === 403) {
            pushTrace({ stage: "rest_auth_blocked", url, status: res.status });
            if (isMounted) setStatus("Server data is protected. Using local saved data for now.");
            continue;
          }
          if (!res.ok) {
            pushTrace({ stage: "rest_http_not_ok", url, status: res.status });
            continue;
          }
          const payload = await res.json();
          const incoming = parseServerSchemaToFields(payload);
          pushTrace({
            stage: "rest_payload_received",
            url,
            incomingCount: incoming.length,
            hasSteps: Array.isArray(payload?.steps),
            hasFields: Array.isArray(payload?.fields),
          });
          if (!incoming.length) {
            pushTrace({ stage: "rest_empty_fields", url });
            continue;
          }
          if (isMounted) {
            setFields(dedupeFields(incoming));
            setStatus(`Loaded ${incoming.length} existing fields from assessment rules.`);
          }
          flushTrace(`Loaded ${incoming.length} existing fields from assessment rules.`);
          return;
        } catch (error) {
          pushTrace({ stage: "rest_fetch_error", url, error: String(error) });
          // Try the next endpoint candidate.
        }
      }

      if (isMounted) setStatus("No existing server data found. You can add fields below.");
      flushTrace("No existing server data found. You can add fields below.");
    };

    loadFromServer();

    return () => {
      isMounted = false;
    };
  }, []);

  const buildBeneficiaryDataSchema = React.useCallback(() => {
    const properties = {};
    const required = [];

    fields.forEach((field) => {
      if (!field?.key || !field?.label) return;
      const key = String(field.key).trim();
      if (!key) return;

      const property = {
        type: mapToJsonSchemaType(field.type),
        title: String(field.label || "").trim(),
      };

      if (field.type === "date") {
        property.format = "date";
      }

      if (field.type === "select" && Array.isArray(field.options) && field.options.length) {
        property.enum = parseOptions(field.options);
      }

      property.x_ui = {
        section: String(field.section || "General").trim(),
        subsection: String(field.subsection || "").trim(),
        help_text: String(field.helpText || "").trim(),
        min_age: String(field.minAge || "").trim() === "" ? null : toSafeInt(field.minAge, 0),
        max_age: String(field.maxAge || "").trim() === "" ? null : toSafeInt(field.maxAge, 0),
        widget: String(field.type || "text").trim(),
      };

      properties[key] = property;
      if (field.required) {
        required.push(key);
      }
    });

    return {
      $schema: "http://json-schema.org/draft-07/schema#",
      type: "object",
      title: "Beneficiary Social UI Schema",
      properties,
      required,
      additionalProperties: true,
    };
  }, [fields]);

  const resolveBenefitPlansForSave = React.useCallback(async () => {
    if (serverBenefitPlans.length) {
      return serverBenefitPlans;
    }

    const apiBases = buildApiBaseCandidates();
    const gqlCandidates = apiBases.map((base) => `${base}/api/graphql`);
    const query = `
      query SocialUiBenefitPlanIds {
        benefitPlan(first: 100) {
          edges {
            node {
              id
              uuid
              code
              name
              type
              dateValidFrom
              dateValidTo
            }
          }
        }
      }
    `;

    for (const gqlUrl of gqlCandidates) {
      try {
        const gqlRes = await fetch(gqlUrl, {
          method: "POST",
          credentials: "include",
          headers: getGraphqlHeaders(),
          body: JSON.stringify({ query }),
        });
        if (!gqlRes.ok) {
          continue;
        }

        const payload = await gqlRes.json();
        const plans = (payload?.data?.benefitPlan?.edges || [])
          .map((edge) => {
            const node = edge?.node || {};
            const id = String(node?.uuid || "").trim();
            if (!id) return null;
            return {
              id,
              code: node?.code,
              name: node?.name,
              type: node?.type,
              dateValidFrom: normalizeGraphQlDate(node?.dateValidFrom),
              dateValidTo: normalizeGraphQlDate(node?.dateValidTo),
            };
          })
          .filter(Boolean);
        if (plans.length) {
          setServerBenefitPlans(plans);
          return plans;
        }
      } catch (error) {
        // Try next candidate.
      }
    }

    const optionCandidates = unique(apiBases.flatMap((base) => {
      const roots = buildApiRoots(base);
      return roots.flatMap((root) => [
        `${root}/sync/social-assessment-rules/options/`,
        `${root}/sync/eligibility-rules/options/`,
      ]);
    }));

    for (const url of optionCandidates) {
      try {
        const res = await fetch(url, { credentials: "include" });
        if (!res.ok) continue;
        const body = await res.json();
        const root = extractPayloadMap(body);
        const plans = extractMapList(root, ["benefit_plans", "benefitPlans", "plans"])
          .map((item) => {
            const uuid = String(item?.uuid || "").trim();
            if (!isUuidLike(uuid)) return null;
            return {
              id: uuid,
              code: item?.code,
              name: item?.name,
            };
          })
          .filter(Boolean);
        if (plans.length) {
          setServerBenefitPlans(plans);
          return plans;
        }
      } catch (error) {
        // Try next candidate.
      }
    }

    return [];
  }, [serverBenefitPlans]);

  const saveToServer = React.useCallback(async () => {
    if (!fields.length) {
      setStatus("Save failed: add at least one field before saving to server.");
      return;
    }

    setSaving(true);
    try {
      const plans = await resolveBenefitPlansForSave();
      if (!plans.length) {
        setStatus("Save failed: no benefit plans found for server save. Check your permissions and active session.");
        return;
      }
      const groupedFieldSections = groupFieldsBySection(fields);
      const fieldsBySection = new Map(
        groupedFieldSections.map((group) => [
          normalizeSectionTitle(group.section),
          group.items.map((x) => x.field),
        ]),
      );
      const sectionNames = unique([
        ...Array.from(sectionMetaByTitle.keys()).map((title) => normalizeSectionTitle(title)),
        ...Array.from(fieldsBySection.keys()),
      ]);

      const sectionGroups = sectionNames
        .map((sectionTitle) => {
          const meta = sectionMetaByTitle.get(sectionTitle)
            || normalizeSectionDefinition({ title: sectionTitle });
          const sectionFields = fieldsBySection.get(sectionTitle) || [];
          return {
            meta,
            section: sectionTitle,
            fields: sectionFields,
            order: meta.order || 999,
            description: meta.description || "",
            minAge: normalizeOptionalInt(meta.minAge),
            maxAge: normalizeOptionalInt(meta.maxAge),
          };
        })
        .sort((a, b) => (a.order - b.order) || a.section.localeCompare(b.section));

      if (!sectionGroups.length) {
        setStatus("Save failed: no valid sections found to save.");
        return;
      }

      const buildSocialRuleUiSchema = (schemaVersion, sectionName, sectionFields, screenOrder, sectionDescription, sectionMinAge, sectionMaxAge) => {
        const sorted = [...sectionFields].sort((a, b) => {
          const ao = normalizeRuleOrder(a?.order, 9999);
          const bo = normalizeRuleOrder(b?.order, 9999);
          if (ao !== bo) return ao - bo;
          return String(a?.label || a?.key || "").localeCompare(String(b?.label || b?.key || ""));
        });
        return {
          schema_version: schemaVersion,
          version: schemaVersion,
          applicability: {
            gender: "any",
            min_age: normalizeOptionalInt(sectionMinAge),
            max_age: normalizeOptionalInt(sectionMaxAge),
          },
          visibility: {
            age_min: normalizeOptionalInt(sectionMinAge),
            age_max: normalizeOptionalInt(sectionMaxAge),
          },
          screen: {
            name: sectionName,
            order: screenOrder,
            description: String(sectionDescription || "").trim(),
          },
          fields: sorted.map((field, index) => ({
            key: String(field.key || "").trim(),
            label: String(field.label || "").trim(),
            label_my: String(field.labelMy || "").trim(),
            type: normalizeFieldType(field.type),
            order: normalizeRuleOrder(field.order, index + 1),
            required: Boolean(field.required),
            section: String(field.section || "General").trim(),
            helper_text: String(field.helpText || "").trim(),
            choices: normalizeFieldType(field.type) === "select" ? parseOptions(field.options || []) : [],
            multiple: normalizeFieldType(field.type) === "select" ? Boolean(field.multiple) : false,
            min_age: null,
            max_age: null,
          })),
        };
      };

      const apiBases = buildApiBaseCandidates();
      const socialEndpoints = buildSocialRuleEndpoints(apiBases);

      let savedCount = 0;
      let skippedCount = 0;
      // Tracked separately from skippedCount: this only increments when an
      // UPDATE to an existing server rule actually failed (as opposed to a
      // section being skipped because nothing about it changed). Previously
      // both cases were lumped into the same "skipped" number, so a real
      // save failure on one section looked identical to "nothing to do" in
      // the final status message — easy to miss, especially when OTHER
      // sections in the same save saved successfully.
      let failedUpdateCount = 0;
      const failedUpdateSections = [];
      let lastError = "";

      const writeRule = async ({ method, urls, payload }) => {
        for (const url of urls) {
          try {
            const res = await fetch(url, {
              method,
              credentials: "include",
              headers: getGraphqlHeaders(),
              body: JSON.stringify(payload),
            });
            if (!res.ok) {
              // 404/405 means the endpoint doesn't exist on this candidate — try next.
              if (res.status === 404 || res.status === 405) continue;
              // Any other error (400, 401, 403, 500...) is a real server rejection —
              // capture it and stop trying more candidates.
              const detail = await res.text();
              let detailText = detail;
              try {
                const parsed = JSON.parse(detail);
                detailText = parsed?.detail || parsed?.message || detail;
              } catch (_) { /* keep raw text */ }
              lastError = `Save failed (${res.status}): ${detailText}`;
              return false;
            }

            const maybeBody = await res.text();
            if (maybeBody) {
              try {
                const parsedBody = JSON.parse(maybeBody);
                if (parsedBody && parsedBody.success === false) {
                  lastError = `Save rejected: ${String(parsedBody.message || parsedBody.detail || "unknown")}`;
                  return false;
                }
              } catch (_) {
                // Non-JSON response can still be valid.
              }
            }
            return true;
          } catch (error) {
            lastError = String(error);
          }
        }
        return false;
      };

      for (const plan of plans) {
        const planId = String(plan?.id || "").trim();
        if (!isUuidLike(planId)) {
          lastError = `Missing/invalid benefit plan uuid (${planId || "empty"})`;
          continue;
        }

        const queryParam = `benefit_plan_id=${encodeURIComponent(planId)}`;
        let existingRulesForPlan = [];
        for (const listUrl of socialEndpoints.rules) {
          try {
            const res = await fetch(`${listUrl}?${queryParam}`, { credentials: "include" });
            if (!res.ok) continue;
            const body = await res.json();
            const rules = extractSocialRulesFromPayload(body)
              .filter((rule) => {
                const action = String(rule?.system_action || "").trim().toLowerCase();
                const rulePlan = rule?.benefit_plan && typeof rule.benefit_plan === "object" ? rule.benefit_plan : {};
                const rulePlanId = String(rulePlan?.uuid || rule?.benefit_plan_id || "").trim();
                return action === "social_assessment" && rulePlanId === planId;
              });
            existingRulesForPlan = rules;
            break;
          } catch (error) {
            // Try next candidate.
          }
        }
        for (let sectionIndex = 0; sectionIndex < sectionGroups.length; sectionIndex += 1) {
          const group = sectionGroups[sectionIndex];
          const sectionName = normalizeSectionTitle(group.section || "General");
          const sectionKey = canonicalSectionKey(sectionName);
          setStatus(
            `Saving section "${sectionName}" (${sectionIndex + 1}/${sectionGroups.length}) for plan ${planId}...`,
          );
          const sectionMatchKeys = new Set([
            sectionKey,
            ...(Array.isArray(group.meta?.aliases) ? group.meta.aliases.map((alias) => canonicalSectionKey(alias)) : []),
          ]);
          const sectionFields = group.fields;

          const existingForSection = existingRulesForPlan
            .filter((rule) => {
              const uiSchema = rule?.ui_schema && typeof rule.ui_schema === "object" ? rule.ui_schema : {};
              const screen = uiSchema?.screen && typeof uiSchema.screen === "object" ? uiSchema.screen : {};
              const candidateKeys = [
                canonicalSectionKey(rule?.name || "General"),
                canonicalSectionKey(screen?.name || ""),
              ];
              return candidateKeys.some((key) => sectionMatchKeys.has(key));
            })
            .sort((a, b) => {
              const aSchema = a?.ui_schema && typeof a.ui_schema === "object" ? a.ui_schema : {};
              const bSchema = b?.ui_schema && typeof b.ui_schema === "object" ? b.ui_schema : {};
              const av = parseVersionNumber(a?.schema_version || aSchema?.schema_version || aSchema?.version, 1);
              const bv = parseVersionNumber(b?.schema_version || bSchema?.schema_version || bSchema?.version, 1);
              return bv - av;
            });

          const latest = existingForSection[0] || null;

          const incomingSignature = buildSectionSignature(sectionName, sectionFields);
          const incomingMetaSignature = buildSectionMetaSignature(group.meta);
          if (latest?.ui_schema && typeof latest.ui_schema === "object") {
            const latestUi = latest.ui_schema && typeof latest.ui_schema === "object"
              ? latest.ui_schema
              : {};
            const latestScreen = latestUi.screen && typeof latestUi.screen === "object"
              ? latestUi.screen
              : {};
            const latestSavedSectionName = normalizeSectionTitle(
              latestScreen.name || latest.name || "General",
            );
            const latestSavedSectionDescription = String(
              latestScreen.description || "",
            ).trim();
            const latestSavedSectionOrder = normalizeRuleOrder(
              latestScreen.order,
              999,
            );
            const latestSavedSectionMinAge = normalizeOptionalInt(
              latestUi?.visibility?.age_min ?? latestUi?.applicability?.min_age ?? latest?.min_age,
            );
            const latestSavedSectionMaxAge = normalizeOptionalInt(
              latestUi?.visibility?.age_max ?? latestUi?.applicability?.max_age ?? latest?.max_age,
            );

            const latestFields = parseServerSchemaToFields(latest.ui_schema)
              .filter((field) => canonicalSectionKey(field?.section || "General") === sectionKey);
            const existingSignature = buildSectionSignature(sectionName, latestFields);
            const incomingSectionDescription = String(group.description || "").trim();
            const incomingSectionOrder = normalizeRuleOrder(group.order, 999);
            const incomingSectionMinAge = normalizeOptionalInt(group.minAge);
            const incomingSectionMaxAge = normalizeOptionalInt(group.maxAge);
            const latestSavedConditions = (Array.isArray(latest.conditions) ? latest.conditions : []).map(
              (c) => ({ field: c.field || "", operator: c.operator || "", value: String(c.value ?? ""), formula: c.formula || "", connector: c.connector || "" }),
            );
            const latestMetaSignature = JSON.stringify({
              title: canonicalSectionKey(latestSavedSectionName),
              description: latestSavedSectionDescription,
              order: latestSavedSectionOrder,
              minAge: latestSavedSectionMinAge,
              maxAge: latestSavedSectionMaxAge,
              conditions: latestSavedConditions,
            });

            const isNameChanged = latestSavedSectionName !== sectionName;
            const isDescriptionChanged = latestSavedSectionDescription !== incomingSectionDescription;
            const isOrderChanged = latestSavedSectionOrder !== incomingSectionOrder;
            const isMinAgeChanged = latestSavedSectionMinAge !== incomingSectionMinAge;
            const isMaxAgeChanged = latestSavedSectionMaxAge !== incomingSectionMaxAge;

            if (!isNameChanged
              && !isDescriptionChanged
              && !isOrderChanged
              && !isMinAgeChanged
              && !isMaxAgeChanged
              && incomingMetaSignature === latestMetaSignature
              && incomingSignature === existingSignature) {
              skippedCount += 1;
              continue;
            }
          }

          const latestSchema = latest?.ui_schema && typeof latest.ui_schema === "object" ? latest.ui_schema : {};
          const nextSchemaVersion = latest
            ? parseVersionNumber(latest?.schema_version || latestSchema?.schema_version || latestSchema?.version, 1) + 1
            : 1;

          const payload = {
            benefit_plan_id: planId,
            name: sectionName,
            enabled: true,
            gender: "any",
            min_age: normalizeOptionalInt(group.minAge),
            max_age: normalizeOptionalInt(group.maxAge),
            diagnosis_id: null,
            service_ids: [],
            conditions: Array.isArray(group.meta?.conditions) ? group.meta.conditions : [],
            ui_schema: buildSocialRuleUiSchema(
              nextSchemaVersion,
              sectionName,
              sectionFields,
              Number.isFinite(Number(group.order)) ? Number(group.order) : sectionIndex + 1,
              group.description,
              group.minAge,
              group.maxAge,
            ),
            mandatory_attachment_required: false,
            mandatory_attachments: false,
            system_action: "social_assessment",
          };

          let savedThisSection = false;
          const existingId = String(latest?.uuid || latest?.rule_id || latest?.id || "").trim();
          let updateAttempted = false;
          if (existingId) {
            updateAttempted = true;
            const detailUrls = socialEndpoints.ruleDetail(existingId);
            savedThisSection = await writeRule({ method: "PUT", urls: detailUrls, payload });
            if (!savedThisSection) {
              savedThisSection = await writeRule({ method: "PATCH", urls: detailUrls, payload });
            }
          }

          if (!savedThisSection) {
            if (updateAttempted) {
              // Do not create a new row when an existing section update target was found but update failed.
              // This avoids unbounded duplicate versions caused by failed detail URL updates.
              // Tracked separately from skippedCount (see declaration above) —
              // this is a real failure to save your changes, not a benign
              // "nothing changed" skip, and must be called out explicitly.
              failedUpdateCount += 1;
              failedUpdateSections.push(`${sectionName} (plan ${planId})`);
              if (!lastError) {
                lastError = `Update failed for existing section ${sectionName} (plan ${planId}); create fallback blocked to prevent duplicates.`;
              }
              continue;
            }
            savedThisSection = await writeRule({ method: "POST", urls: socialEndpoints.rules, payload });
          }

          if (savedThisSection) {
            savedCount += 1;
          } else if (!lastError) {
            lastError = `Failed to save section ${sectionName} for benefit plan ${planId}`;
          }
        }

        // Delete server-side rules that no longer have a matching section in the UI.
        const currentSectionKeys = new Set(
          sectionGroups.flatMap((g) => {
            const key = canonicalSectionKey(g.section);
            const aliases = Array.isArray(g.meta?.aliases)
              ? g.meta.aliases.map((a) => canonicalSectionKey(a))
              : [];
            return [key, ...aliases];
          }),
        );
        for (const serverRule of existingRulesForPlan) {
          const uiSchema = serverRule?.ui_schema && typeof serverRule.ui_schema === "object"
            ? serverRule.ui_schema : {};
          const screen = uiSchema?.screen && typeof uiSchema.screen === "object"
            ? uiSchema.screen : {};
          const serverSectionKey = canonicalSectionKey(screen?.name || serverRule?.name || "");
          if (currentSectionKeys.has(serverSectionKey)) continue;

          const ruleId = String(serverRule?.uuid || serverRule?.rule_id || serverRule?.id || "").trim();
          if (!ruleId) continue;

          const deleteUrls = socialEndpoints.ruleDetail(ruleId);
          for (const url of deleteUrls) {
            try {
              const res = await fetch(url, { method: "DELETE", credentials: "include" });
              if (res.ok || res.status === 204 || res.status === 404) break;
              if (res.status === 405) continue;
            } catch (_) { /* ignore network errors on delete */ }
          }
        }
      }

      if (!savedCount) {
        if (failedUpdateCount > 0) {
          setStatus(
            `Save FAILED — 0 sections saved. ${failedUpdateCount} section(s) could not be updated: `
              + `${failedUpdateSections.join(", ")}. ${lastError || ""}`,
          );
          return;
        }
        if (lastError) {
          setStatus(`Save failed. ${lastError}`);
          return;
        }
        if (skippedCount > 0) {
          setStatus(`No section changes detected to save (${skippedCount} section-plan pair(s) skipped).`);
          return;
        }
        setStatus("Save failed. No writable social rules endpoint found.");
        return;
      }

      // failedUpdateCount is called out here EVEN THOUGH savedCount > 0 —
      // a partial failure (some sections saved, others silently failed to
      // update) previously fell straight through to the plain success
      // message below with no mention of the failure at all. This is
      // exactly the scenario that made a section look "saved" (since other
      // sections in the same save genuinely succeeded) when it actually
      // never reached the server.
      if (failedUpdateCount > 0) {
        setStatus(
          `Saved ${savedCount} section rule(s), but FAILED to save ${failedUpdateCount} section(s): `
            + `${failedUpdateSections.join(", ")}. These were NOT saved to the server — `
            + `check your session/permissions and save again.`,
        );
        return;
      }

      setStatus(`Saved ${savedCount} changed social assessment section rule(s) across ${plans.length} benefit plan(s); skipped ${skippedCount} unchanged/missing section-plan pair(s).`);
    } finally {
      setSaving(false);
    }
  }, [fields, resolveBenefitPlansForSave, sectionMetaByTitle]);

  const upsertSection = () => {
    const title = normalizeSectionTitle(sectionForm.title);
    if (!title) {
      setStatus("Section title is required.");
      return;
    }

    const sectionMinAge = String(sectionForm.minAge || "").trim() === "" ? "" : toSafeInt(sectionForm.minAge, 0);
    const sectionMaxAge = String(sectionForm.maxAge || "").trim() === "" ? "" : toSafeInt(sectionForm.maxAge, 0);
    if (sectionMinAge !== "" && sectionMaxAge !== "" && sectionMinAge > sectionMaxAge) {
      setStatus("Section min age cannot be greater than section max age.");
      return;
    }

    setSections((prev) => {
      const idx = prev.findIndex((section) => normalizeSectionTitle(section.title) === normalizeSectionTitle(sectionForm.originalTitle || title));
      if (idx < 0) {
        const next = normalizeSectionDefinition({
          title,
          description: sectionForm.description,
          order: sectionForm.order,
          minAge: sectionMinAge,
          maxAge: sectionMaxAge,
        });
        return [...prev, next];
      }

      const current = normalizeSectionDefinition(prev[idx]);
      const aliases = unique([
        ...(Array.isArray(current.aliases) ? current.aliases : []),
        ...(sectionForm.originalTitle && sectionForm.originalTitle !== title ? [sectionForm.originalTitle] : []),
      ]).filter((alias) => normalizeSectionTitle(alias) !== title);

      const next = normalizeSectionDefinition({
        title,
        description: sectionForm.description,
        order: sectionForm.order,
        minAge: sectionMinAge,
        maxAge: sectionMaxAge,
        aliases,
      });

      const clone = [...prev];
      clone[idx] = next;
      return clone;
    });

    if (sectionForm.originalTitle && sectionForm.originalTitle !== title) {
      const previousTitle = normalizeSectionTitle(sectionForm.originalTitle);
      setFields((prev) => prev.map((field) => (
        normalizeSectionTitle(field.section) === previousTitle
          ? { ...field, section: title }
          : field
      )));
    }

    setForm((prev) => {
      if (!prev.section || normalizeSectionTitle(prev.section) === normalizeSectionTitle(sectionForm.originalTitle)) {
        return { ...prev, section: title };
      }
      return prev;
    });

    setSectionForm({ originalTitle: "", title: "", description: "", order: "", minAge: "", maxAge: "" });
    setUseCustomSection(false);
    setStatus(`Section ${title} saved.`);
  };

  const editSection = (title) => {
    const target = sectionMetaByTitle.get(normalizeSectionTitle(title));
    if (!target) return;
    setSectionForm({
      originalTitle: target.title,
      title: target.title,
      description: target.description || "",
      order: String(target.order || ""),
      minAge: target.minAge ?? "",
      maxAge: target.maxAge ?? "",
    });
  };

  const removeSection = (title) => {
    const normalizedTitle = normalizeSectionTitle(title);
    const usedCount = fields.filter((field) => normalizeSectionTitle(field.section) === normalizedTitle).length;
    if (usedCount > 0) {
      setStatus(`Cannot remove section ${normalizedTitle}. Remove or move its ${usedCount} field(s) first.`);
      return;
    }
    setSections((prev) => prev.filter((section) => normalizeSectionTitle(section.title) !== normalizedTitle));
    if (normalizeSectionTitle(sectionForm.originalTitle) === normalizedTitle) {
      setSectionForm({ originalTitle: "", title: "", description: "", order: "", minAge: "", maxAge: "" });
    }
  };

  const upsertField = () => {
    if (!form.label.trim() || !form.key.trim() || !form.section.trim()) {
      return;
    }
    const options = parseOptions(form.options || []);
    if (form.type === "select" && !options.length) {
      setStatus("Select fields require options. Add values like: no disability, severe, non severe.");
      return;
    }

    let nextOrder = normalizeRuleOrder(form.order, null);
    if (nextOrder == null) {
      const sectionCount = fields.filter((field) => normalizeSectionTitle(field.section) === normalizeSectionTitle(form.section)).length;
      nextOrder = sectionCount + 1;
    }

    const normalized = normalizeField({ ...form, order: nextOrder, minAge: "", maxAge: "", options });
    setFields((prev) => {
      if (editIndex === null) {
        return [...prev, normalized];
      }
      return prev.map((field, index) => (index === editIndex ? normalized : field));
    });
    setEditIndex(null);
    setForm((prev) => ({
      ...prev,
      label: "",
      key: "",
      section: "",
      subsection: "",
      order: "",
      helpText: "",
      required: false,
      options: [],
    }));
    setOptionDraft("");
    setUseCustomSection(true);
  };

  const removeField = (index) => setFields((prev) => prev.filter((_, i) => i !== index));
  const editField = (index) => {
    const target = fields[index];
    setForm({
      ...target,
      options: parseOptions(target.options || []),
    });
    setOptionDraft("");
    setUseCustomSection(!sectionOptions.includes(String(target.section || "")));
    setEditIndex(index);
  };
  const cancelEdit = () => {
    setEditIndex(null);
    setForm({
      label: "",
      key: "",
      section: "",
      subsection: "",
      type: "text",
      order: "",
      helpText: "",
      required: false,
      options: [],
    });
    setOptionDraft("");
    setUseCustomSection(true);
  };

  const autoKey = (label) =>
    String(label || "").trim().toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");

  const blankInlineRule = () => ({
    id: `ir_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    label: "",
    key: "",
    type: "text",
    required: false,
    options: [],
    optionDraft: "",
  });

  const openInlineSection = (secTitle) => {
    if (secTitle) {
      const meta = sectionMetaByTitle.get(normalizeSectionTitle(secTitle)) || {};
      const secFields = fields
        .filter((f) => normalizeSectionTitle(f.section) === normalizeSectionTitle(secTitle))
        .sort((a, b) => normalizeRuleOrder(a.order, 9999) - normalizeRuleOrder(b.order, 9999));
      setInlineSection({
        originalTitle: meta.title || secTitle,
        title: meta.title || secTitle,
        description: meta.description || "",
        order: String(meta.order || ""),
        minAge: meta.minAge != null ? String(meta.minAge) : "",
        maxAge: meta.maxAge != null ? String(meta.maxAge) : "",
        conditions: Array.isArray(meta.conditions) ? meta.conditions.map((c) => ({ ...c, connector: c.connector === "or" ? "or" : "and", id: `cond_${Date.now()}_${Math.random().toString(36).slice(2, 5)}` })) : [],
        rules: secFields.map((f) => ({
          id: f.key || `ir_${Date.now()}`,
          label: f.label || "",
          labelMy: f.labelMy || "",
          key: f.key || "",
          type: f.type || "text",
          required: Boolean(f.required),
          options: parseOptions(f.options || []),
          optionDraft: "",
          multiple: Boolean(f.multiple),
        })),
      });
    } else {
      const nextOrder = sections.length + 1;
      setInlineSection({ originalTitle: "", title: "", description: "", order: String(nextOrder), minAge: "", maxAge: "", conditions: [], rules: [] });
    }
  };

  const closeInlineSection = () => setInlineSection(null);

  const updateInlineRule = (ruleId, updates) => {
    setInlineSection((prev) => ({
      ...prev,
      rules: prev.rules.map((r) => {
        if (r.id !== ruleId) return r;
        const next = { ...r, ...updates };
        if ("label" in updates && !updates.key && r.key === autoKey(r.label)) {
          next.key = autoKey(updates.label);
        }
        return next;
      }),
    }));
  };

  const addOptionToInlineRule = (ruleId) => {
    setInlineSection((prev) => ({
      ...prev,
      rules: prev.rules.map((r) => {
        if (r.id !== ruleId) return r;
        const val = String(r.optionDraft || "").trim();
        if (!val) return r;
        return { ...r, options: unique([...r.options, val]), optionDraft: "" };
      }),
    }));
  };

  const removeOptionFromInlineRule = (ruleId, opt) => {
    setInlineSection((prev) => ({
      ...prev,
      rules: prev.rules.map((r) =>
        r.id === ruleId ? { ...r, options: r.options.filter((o) => o !== opt) } : r,
      ),
    }));
  };

  const commitInlineSection = () => {
    if (!inlineSection) return;
    const title = normalizeSectionTitle(inlineSection.title);
    if (!title) { setStatus("Section title is required."); return; }
    const validRules = inlineSection.rules.filter((r) => r.label.trim() && r.key.trim());
    if (!validRules.length) { setStatus("Add at least one rule with a label and key."); return; }

    const sectionMinAge = String(inlineSection.minAge || "").trim() === "" ? "" : toSafeInt(inlineSection.minAge, 0);
    const sectionMaxAge = String(inlineSection.maxAge || "").trim() === "" ? "" : toSafeInt(inlineSection.maxAge, 0);
    if (sectionMinAge !== "" && sectionMaxAge !== "" && sectionMinAge > sectionMaxAge) {
      setStatus("Min age cannot be greater than max age."); return;
    }

    setSections((prev) => {
      const originalTitle = normalizeSectionTitle(inlineSection.originalTitle || "");
      const idx = prev.findIndex((s) => normalizeSectionTitle(s.title) === (originalTitle || title));
      const next = normalizeSectionDefinition({
        title,
        description: inlineSection.description,
        order: inlineSection.order,
        minAge: sectionMinAge,
        maxAge: sectionMaxAge,
        conditions: (inlineSection.conditions || [])
          .filter((c) => c.field && c.operator)
          .map(({ field, operator, value, formula, connector }, i) => {
            const validOpKeys = CONDITION_OPERATORS.map((o) => o.key);
            let op = (operator || "").toLowerCase().trim();
            if (op === "equals" || op === "equal") op = "eq";
            else if (op === "not_equals" || op === "notequals" || op === "neq" || op === "not_eq") op = "ne";
            if (!validOpKeys.includes(op)) op = "eq";
            const conn = i > 0 ? (connector === "or" ? "or" : "and") : undefined;
            return field === "_formula"
              ? { field, operator: op, value, formula: formula || "", ...(conn !== undefined ? { connector: conn } : {}) }
              : { field, operator: op, value, ...(conn !== undefined ? { connector: conn } : {}) };
          }),
        aliases: idx >= 0 && originalTitle && originalTitle !== title
          ? unique([
              ...(Array.isArray(normalizeSectionDefinition(prev[idx]).aliases) ? normalizeSectionDefinition(prev[idx]).aliases : []),
              originalTitle,
            ]).filter((a) => normalizeSectionTitle(a) !== title)
          : [],
      });
      if (idx < 0) return [...prev, next];
      const clone = [...prev];
      clone[idx] = next;
      return clone;
    });

    if (inlineSection.originalTitle && normalizeSectionTitle(inlineSection.originalTitle) !== title) {
      const previousTitle = normalizeSectionTitle(inlineSection.originalTitle);
      setFields((prev) =>
        prev.map((f) => (normalizeSectionTitle(f.section) === previousTitle ? { ...f, section: title } : f)),
      );
    }

    const newFields = validRules.map((rule, idx) =>
      normalizeField({
        label: rule.label.trim(),
        label_my: (rule.labelMy || "").trim(),
        key: rule.key.trim(),
        section: title,
        subsection: "",
        type: rule.type || "text",
        order: idx + 1,
        helpText: "",
        required: Boolean(rule.required),
        options: parseOptions(rule.options || []),
        multiple: rule.type === "select" ? Boolean(rule.multiple) : false,
      }),
    );

    setFields((prev) => {
      const targetTitle = normalizeSectionTitle(inlineSection.originalTitle || title);
      return [...prev.filter((f) => normalizeSectionTitle(f.section) !== targetTitle), ...newFields];
    });

    setStatus(`Section "${title}" saved with ${validRules.length} rule(s).`);
    closeInlineSection();
  };

  // Deletes the server-side social-assessment rule(s) matching a section, across
  // every benefit plan. This mirrors the orphan-cleanup reconciliation that
  // saveToServer runs at the end of a save (see the "Delete server-side rules
  // that no longer have a matching section" block above), but is triggered
  // immediately from the delete button instead of only when the user next
  // clicks "Save to Server" — without this, a section removed here only
  // disappears from local/draft state and reappears after a page refresh
  // because the server-side row was never actually deleted.
  const deleteSectionRulesFromServer = async (sectionMatchKeys) => {
    const plans = await resolveBenefitPlansForSave();
    if (!plans.length) return;

    const apiBases = buildApiBaseCandidates();
    const socialEndpoints = buildSocialRuleEndpoints(apiBases);

    for (const plan of plans) {
      const planId = String(plan?.id || "").trim();
      if (!isUuidLike(planId)) continue;

      const queryParam = `benefit_plan_id=${encodeURIComponent(planId)}`;
      let existingRulesForPlan = [];
      for (const listUrl of socialEndpoints.rules) {
        try {
          const res = await fetch(`${listUrl}?${queryParam}`, { credentials: "include" });
          if (!res.ok) continue;
          const body = await res.json();
          existingRulesForPlan = extractSocialRulesFromPayload(body).filter((rule) => {
            const action = String(rule?.system_action || "").trim().toLowerCase();
            const rulePlan = rule?.benefit_plan && typeof rule.benefit_plan === "object" ? rule.benefit_plan : {};
            const rulePlanId = String(rulePlan?.uuid || rule?.benefit_plan_id || "").trim();
            return action === "social_assessment" && rulePlanId === planId;
          });
          break;
        } catch (_) {
          // Try next candidate.
        }
      }

      for (const serverRule of existingRulesForPlan) {
        const uiSchema = serverRule?.ui_schema && typeof serverRule.ui_schema === "object" ? serverRule.ui_schema : {};
        const screen = uiSchema?.screen && typeof uiSchema.screen === "object" ? uiSchema.screen : {};
        const serverSectionKey = canonicalSectionKey(screen?.name || serverRule?.name || "");
        if (!sectionMatchKeys.has(serverSectionKey)) continue;

        const ruleId = String(serverRule?.uuid || serverRule?.rule_id || serverRule?.id || "").trim();
        if (!ruleId) continue;

        const deleteUrls = socialEndpoints.ruleDetail(ruleId);
        for (const url of deleteUrls) {
          try {
            const res = await fetch(url, { method: "DELETE", credentials: "include" });
            if (res.ok || res.status === 204 || res.status === 404) break;
            if (res.status === 405) continue;
          } catch (_) { /* ignore network errors on delete */ }
        }
      }
    }
  };

  const deleteSection = (secTitle) => {
    const normalizedTitle = normalizeSectionTitle(secTitle);
    if (!window.confirm(`Delete section "${secTitle}" and all its rules?`)) return;

    const meta = sectionMetaByTitle.get(normalizedTitle);
    const sectionMatchKeys = new Set([
      canonicalSectionKey(normalizedTitle),
      ...(Array.isArray(meta?.aliases) ? meta.aliases.map((alias) => canonicalSectionKey(alias)) : []),
    ]);

    setSections((prev) => prev.filter((s) => normalizeSectionTitle(s.title) !== normalizedTitle));
    setFields((prev) => prev.filter((f) => normalizeSectionTitle(f.section) !== normalizedTitle));
    setStatus(`Section "${secTitle}" deleted. Removing it from the server...`);

    deleteSectionRulesFromServer(sectionMatchKeys)
      .then(() => setStatus(`Section "${secTitle}" deleted.`))
      .catch(() => setStatus(
        `Failed to confirm section "${secTitle}" was removed from the server (deleted locally). `
          + 'Click "Save to Server" to retry.',
      ));
  };

  const INLINE_TYPE_LABELS = { text: "Text", number: "Number", boolean: "Yes / No", select: "Options", date: "Date" };

  const TYPE_COLORS = {
    text: { bg: "#EFF6FF", color: "#1D4ED8" },
    number: { bg: "#F0FDF4", color: "#166534" },
    boolean: { bg: "#FFF7ED", color: "#C2410C" },
    select: { bg: "#FAF5FF", color: "#7C3AED" },
    date: { bg: "#F0F9FF", color: "#0369A1" },
  };

  return (
    <Box style={{ background: "#F1F5F9", minHeight: "100vh", padding: "0 0 48px" }}>

      {/* ── Page header ───────────────────────────────────────────────────── */}
      <Box style={{ background: "linear-gradient(135deg,#0F766E 0%,#0D9488 100%)", padding: "22px 32px 20px" }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" style={{ flexWrap: "wrap", gap: 12 }}>
          <Box>
            <Typography style={{ fontSize: 28, fontWeight: 800, color: "#fff", letterSpacing: "-0.4px", lineHeight: 1.15 }}>
              Social Assessment Builder
            </Typography>
            <Box display="flex" alignItems="center" style={{ gap: 16, marginTop: 8, flexWrap: "wrap" }}>
              <Box style={{ background: "rgba(255,255,255,0.18)", borderRadius: 20, padding: "3px 12px" }}>
                <Typography style={{ fontSize: 13, color: "#fff", fontWeight: 600 }}>
                  {groupedConfiguredFields.length} section{groupedConfiguredFields.length !== 1 ? "s" : ""}
                </Typography>
              </Box>
              <Box style={{ background: "rgba(255,255,255,0.18)", borderRadius: 20, padding: "3px 12px" }}>
                <Typography style={{ fontSize: 13, color: "#fff", fontWeight: 600 }}>
                  {fields.length} field{fields.length !== 1 ? "s" : ""}
                </Typography>
              </Box>
              {/* Previously this always showed a hardcoded "Saving…" while
                  saving===true and completely ignored the live `status` text
                  set throughout saveToServer's per-section loop — so none of
                  the "saving section X of Y" progress messages were ever
                  visible, only a generic spinner label, no matter how
                  detailed the underlying status tracking was. */}
              {saving && (
                <Typography style={{ fontSize: 13, color: "rgba(255,255,255,0.8)" }}>
                  {status || "Saving…"}
                </Typography>
              )}
              {/* Also previously used a rigid status.startsWith("Saved"/
                  "Error"/"Failed") check — anything else (e.g. "No benefit
                  plans found for server save...", or a partial-failure
                  message that happens to start with "Saved N section(s),
                  but FAILED...") either rendered invisibly or with the wrong
                  (success-green) color. Detect failure by content instead of
                  a brittle exact prefix, and always show SOME status once
                  saving finishes rather than silently showing nothing. */}
              {!saving && status && /fail|error/i.test(status) && (
                <Typography style={{ fontSize: 13, color: "#FCA5A5" }}>⚠ {status}</Typography>
              )}
              {!saving && status && !/fail|error/i.test(status) && (
                <Typography style={{ fontSize: 13, color: "#A7F3D0" }}>✓ {status}</Typography>
              )}
            </Box>
          </Box>
          <Button
            variant="contained"
            onClick={saveToServer}
            disabled={saving || fields.length === 0}
            style={{
              background: saving || fields.length === 0 ? "rgba(255,255,255,0.25)" : "#fff",
              color: saving || fields.length === 0 ? "rgba(255,255,255,0.45)" : "#0F766E",
              fontWeight: 800,
              fontSize: 15,
              padding: "11px 28px",
              borderRadius: 10,
              boxShadow: "none",
              textTransform: "none",
              minWidth: 160,
            }}
          >
            {saving ? "Saving…" : "Save to Server"}
          </Button>
        </Box>
      </Box>

      <Box style={{ padding: "28px 32px 0" }}>

        {/* ── Existing sections ──────────────────────────────────────────── */}
        {groupedConfiguredFields.length === 0 && !inlineSection && (
          <Paper style={{ borderRadius: 16, padding: "60px 32px", textAlign: "center", marginBottom: 24, border: "2px dashed #CBD5E1", background: "#fff" }}>
            <Typography style={{ fontSize: 22, fontWeight: 800, color: "#475569", marginBottom: 10 }}>No sections yet</Typography>
            <Typography style={{ fontSize: 16, color: "#94A3B8", marginBottom: 28 }}>
              Click below to create your first section and add assessment questions to it.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={() => openInlineSection(null)}
              style={{ borderRadius: 10, textTransform: "none", fontWeight: 700, fontSize: 16, padding: "12px 32px" }}
            >
              Add First Section
            </Button>
          </Paper>
        )}

        {groupedConfiguredFields.map((group, groupIdx) => {
          const meta = sectionMetaByTitle.get(normalizeSectionTitle(group.section)) || {};
          const sortedItems = [...group.items].sort((a, b) => {
            const ao = normalizeRuleOrder(a?.field?.order, 9999);
            const bo = normalizeRuleOrder(b?.field?.order, 9999);
            if (ao !== bo) return ao - bo;
            return String(a?.field?.label || "").localeCompare(String(b?.field?.label || ""));
          });
          const isBeingEdited = inlineSection && normalizeSectionTitle(inlineSection.originalTitle || inlineSection.title) === normalizeSectionTitle(group.section);
          const conditionCount = Array.isArray(meta.conditions) ? meta.conditions.length : 0;

          if (isBeingEdited) return null;
          return (
            <Paper
              key={group.section}
              elevation={2}
              style={{ marginBottom: 16, borderRadius: 16, overflow: "hidden" }}
            >
              {/* Section header */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                style={{ padding: "18px 24px", background: "linear-gradient(135deg,#0F766E 0%,#0D9488 100%)" }}
              >
                <Box>
                  <Box display="flex" alignItems="center" style={{ gap: 12, marginBottom: 6 }}>
                    <Box style={{ background: "rgba(255,255,255,0.25)", borderRadius: 8, padding: "3px 11px" }}>
                      <Typography style={{ fontSize: 13, fontWeight: 800, color: "#fff" }}>
                        {meta.order != null ? `#${meta.order}` : `#${groupIdx + 1}`}
                      </Typography>
                    </Box>
                    <Typography style={{ fontSize: 20, fontWeight: 800, color: "#fff", letterSpacing: "-0.3px" }}>
                      {group.section}
                    </Typography>
                  </Box>
                  <Box display="flex" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <Box style={{ background: "rgba(255,255,255,0.2)", borderRadius: 20, padding: "4px 12px" }}>
                      <Typography style={{ fontSize: 13, color: "#fff", fontWeight: 600 }}>
                        {sortedItems.length} question{sortedItems.length !== 1 ? "s" : ""}
                      </Typography>
                    </Box>
                    {(meta.minAge != null || meta.maxAge != null) && (
                      <Box style={{ background: "rgba(255,255,255,0.2)", borderRadius: 20, padding: "4px 12px" }}>
                        <Typography style={{ fontSize: 13, color: "#fff", fontWeight: 600 }}>
                          Age {meta.minAge ?? "0"}–{meta.maxAge ?? "∞"} yrs
                        </Typography>
                      </Box>
                    )}
                    {conditionCount > 0 && (
                      <Box style={{ background: "rgba(251,191,36,0.3)", borderRadius: 20, padding: "4px 12px" }}>
                        <Typography style={{ fontSize: 13, color: "#FEF3C7", fontWeight: 700 }}>
                          {conditionCount} eligibility condition{conditionCount !== 1 ? "s" : ""}
                        </Typography>
                      </Box>
                    )}
                    {meta.description ? (
                      <Typography style={{ fontSize: 13, color: "rgba(255,255,255,0.75)", marginLeft: 4 }}>{meta.description}</Typography>
                    ) : null}
                  </Box>
                </Box>
                <Box display="flex" style={{ gap: 6 }}>
                  <IconButton
                    size="small"
                    onClick={() => openInlineSection(group.section)}
                    style={{ color: "#fff", background: "rgba(255,255,255,0.18)", borderRadius: 8, padding: 8 }}
                  >
                    <EditOutlined />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => deleteSection(group.section)}
                    style={{ color: "#FCA5A5", background: "rgba(239,68,68,0.18)", borderRadius: 8, padding: 8 }}
                  >
                    <DeleteOutline />
                  </IconButton>
                </Box>
              </Box>

              {/* Questions list */}
              <Box style={{ padding: "4px 24px 20px", background: "#fff" }}>
                {sortedItems.map(({ field }, idx) => {
                  const typeColor = TYPE_COLORS[field.type] || { bg: "#F3F4F6", color: "#374151" };
                  const opts = field.type === "select" ? parseOptions(field.options || []) : [];
                  return (
                    <Box
                      key={field.key}
                      style={{
                        padding: "14px 0",
                        borderBottom: idx < sortedItems.length - 1 ? "1px solid #F1F5F9" : "none",
                      }}
                    >
                      <Box display="flex" alignItems="center" style={{ gap: 14 }}>
                        <Typography style={{ color: "#CBD5E1", fontSize: 15, fontWeight: 700, minWidth: 28, textAlign: "right", flexShrink: 0 }}>
                          {idx + 1}
                        </Typography>
                        <Typography style={{ fontSize: 16, color: "#111827", fontWeight: 500, flex: 1, lineHeight: 1.4 }}>
                          {field.label}
                          {field.required && <span style={{ color: "#EF4444", marginLeft: 5 }}>*</span>}
                        </Typography>
                        <Box style={{ background: typeColor.bg, borderRadius: 20, padding: "4px 14px", flexShrink: 0 }}>
                          <Typography style={{ fontSize: 13, color: typeColor.color, fontWeight: 700 }}>
                            {field.type === "boolean" ? "Yes / No" : INLINE_TYPE_LABELS[field.type] || field.type}
                          </Typography>
                        </Box>
                      </Box>
                      {opts.length > 0 && (
                        <Box style={{ marginTop: 6, marginLeft: 42, display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {opts.map((opt, oi) => (
                            <Box key={oi} style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 6, padding: "3px 10px" }}>
                              <Typography style={{ fontSize: 13, color: "#475569" }}>{opt}</Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  );
                })}

                {/* Conditions summary */}
                {conditionCount > 0 && (
                  <Box style={{ marginTop: 10, padding: "8px 12px", background: "#FFFBEB", borderRadius: 8, border: "1px solid #FDE68A" }}>
                    <Typography style={{ fontSize: 12, fontWeight: 700, color: "#92400E", marginBottom: 4 }}>
                      Eligibility Conditions
                    </Typography>
                    {meta.conditions.map((c, ci) => (
                      <Typography key={ci} style={{ fontSize: 13, color: "#78350F" }}>
                        {ci > 0 && (
                          <span style={{ color: "#B45309", fontWeight: 700 }}>
                            {" "}{(c.connector === "or" ? "OR" : "AND")}{" "}
                          </span>
                        )}
                        <strong>{(SOCIAL_CONDITION_FIELDS.find((f) => f.key === c.field) || groupedConfiguredFields.flatMap((g) => g.items).map(({ field }) => field).find((f) => `form.${f.key}` === c.field) || { label: c.field }).label}</strong>
                        {" "}{(CONDITION_OPERATORS.find((o) => o.key === c.operator) || { label: c.operator }).label}
                        {" "}<strong>{c.value}</strong>
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            </Paper>
          );
        })}

        {/* ── Inline section builder ────────────────────────────────────── */}
        {inlineSection ? (
          <Paper elevation={3} style={{ borderRadius: 16, overflow: "hidden", marginBottom: 20 }}>

            {/* Builder header */}
            <Box style={{ background: "linear-gradient(135deg,#0F766E 0%,#0D9488 100%)", padding: "20px 28px" }}>
              <Typography style={{ fontSize: 22, fontWeight: 800, color: "#fff", letterSpacing: "-0.3px" }}>
                {inlineSection.originalTitle ? `Editing: ${inlineSection.originalTitle}` : "New Section"}
              </Typography>
            </Box>

            <Box style={{ padding: "24px 28px" }}>

              {/* ── Section details ── */}
              <Typography style={{ fontSize: 13, fontWeight: 700, color: "#0F766E", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 10 }}>
                Section Details
              </Typography>
              <Box display="flex" style={{ gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
                <TextField
                  label="Section Title *"
                  value={inlineSection.title}
                  onChange={(e) => setInlineSection((prev) => ({ ...prev, title: e.target.value }))}
                  variant="outlined"
                  style={{ flex: "3 1 200px" }}
                />
                <TextField
                  label="Order"
                  type="number"
                  inputProps={{ min: 1 }}
                  value={inlineSection.order}
                  onChange={(e) => setInlineSection((prev) => ({ ...prev, order: e.target.value }))}
                  variant="outlined"
                  style={{ flex: "1 1 80px" }}
                />
                <TextField
                  label="Description (optional)"
                  value={inlineSection.description}
                  onChange={(e) => setInlineSection((prev) => ({ ...prev, description: e.target.value }))}
                  variant="outlined"
                  style={{ flex: "4 1 220px" }}
                />
                <TextField
                  label="Min Age (yrs)"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={inlineSection.minAge}
                  onChange={(e) => setInlineSection((prev) => ({ ...prev, minAge: e.target.value }))}
                  variant="outlined"
                  style={{ flex: "1 1 100px" }}
                />
                <TextField
                  label="Max Age (yrs)"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={inlineSection.maxAge}
                  onChange={(e) => setInlineSection((prev) => ({ ...prev, maxAge: e.target.value }))}
                  variant="outlined"
                  style={{ flex: "1 1 100px" }}
                />
              </Box>

              {/* ── Questions ── */}
              <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 10 }}>
                <Typography style={{ fontSize: 13, fontWeight: 700, color: "#0F766E", textTransform: "uppercase", letterSpacing: "0.8px" }}>
                  Questions
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<Add />}
                  onClick={() => setInlineSection((prev) => ({ ...prev, rules: [...prev.rules, blankInlineRule()] }))}
                  style={{ borderRadius: 8, textTransform: "none", fontWeight: 600 }}
                >
                  Add Question
                </Button>
              </Box>

              {!inlineSection.rules.length && (
                <Box style={{ padding: "20px", background: "#F8FAFC", borderRadius: 12, textAlign: "center", marginBottom: 16, border: "2px dashed #CBD5E1" }}>
                  <Typography style={{ color: "#94A3B8", fontSize: 14 }}>No questions yet — click Add Question</Typography>
                </Box>
              )}

              {inlineSection.rules.map((rule, ruleIdx) => {
                const typeColor = TYPE_COLORS[rule.type] || { bg: "#F3F4F6", color: "#374151" };
                return (
                  <Box
                    key={rule.id}
                    style={{
                      border: "1px solid #E2E8F0",
                      borderLeft: `4px solid ${typeColor.color}`,
                      borderRadius: 12,
                      padding: "14px 16px",
                      marginBottom: 10,
                      background: "#fff",
                    }}
                  >
                    <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 10 }}>
                      <Box style={{ background: "#F1F5F9", borderRadius: 6, padding: "2px 8px", minWidth: 28, textAlign: "center" }}>
                        <Typography style={{ fontSize: 12, fontWeight: 700, color: "#64748B" }}>{ruleIdx + 1}</Typography>
                      </Box>
                      <Typography style={{ fontSize: 13, fontWeight: 700, color: "#475569", flex: 1 }}>Question {ruleIdx + 1}</Typography>
                      <IconButton
                        size="small"
                        onClick={() => setInlineSection((prev) => ({ ...prev, rules: prev.rules.filter((r) => r.id !== rule.id) }))}
                        style={{ color: "#EF4444" }}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Box>

                    <Box display="flex" style={{ gap: 12, flexWrap: "wrap" }}>
                      <TextField
                        label="Question / Label *"
                        value={rule.label}
                        onChange={(e) => updateInlineRule(rule.id, { label: e.target.value })}
                        variant="outlined"
                        style={{ flex: "4 1 220px" }}
                      />
                      <TextField
                        label="Label (Myanmar) — optional"
                        value={rule.labelMy || ""}
                        onChange={(e) => updateInlineRule(rule.id, { labelMy: e.target.value })}
                        variant="outlined"
                        placeholder="မြန်မာဘာသာဖြင့် မေးခွန်း"
                        style={{ flex: "4 1 220px" }}
                      />
                      <TextField
                        select
                        label="Answer Type"
                        value={rule.type}
                        onChange={(e) => updateInlineRule(rule.id, { type: e.target.value, options: [] })}
                        variant="outlined"
                        style={{ flex: "2 1 140px" }}
                      >
                        {FIELD_TYPES.map((t) => (
                          <MenuItem key={t} value={t}>
                            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                              <Box style={{ width: 8, height: 8, borderRadius: "50%", background: (TYPE_COLORS[t] || {}).color || "#374151" }} />
                              {INLINE_TYPE_LABELS[t] || t}
                            </Box>
                          </MenuItem>
                        ))}
                      </TextField>
                      <Box style={{ paddingTop: 4 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={rule.required}
                              onChange={(e) => updateInlineRule(rule.id, { required: e.target.checked })}
                              color="primary"
                            />
                          }
                          label={<Typography style={{ fontSize: 14, fontWeight: 500 }}>Required</Typography>}
                        />
                      </Box>
                    </Box>

                    {rule.type === "boolean" && (
                      <Box style={{ marginTop: 8, padding: "6px 12px", background: "#FFF7ED", borderRadius: 8, display: "inline-block" }}>
                        <Typography style={{ fontSize: 13, color: "#C2410C" }}>Answer options: Yes · No (automatic)</Typography>
                      </Box>
                    )}

                    {rule.type === "select" && (
                      <Box style={{ marginTop: 10 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(rule.multiple)}
                              onChange={(e) => updateInlineRule(rule.id, { multiple: e.target.checked })}
                              color="primary"
                            />
                          }
                          label={
                            <Typography style={{ fontSize: 13 }}>
                              Allow multiple selections (beneficiary can pick more than one option)
                            </Typography>
                          }
                        />
                        <Box display="flex" style={{ gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                          {rule.options.map((opt) => (
                            <Chip
                              key={`${rule.id}-${opt}`}
                              label={opt}
                              size="small"
                              onDelete={() => removeOptionFromInlineRule(rule.id, opt)}
                              style={{ background: "#FAF5FF", color: "#7C3AED", fontWeight: 500 }}
                            />
                          ))}
                          {!rule.options.length && (
                            <Typography style={{ color: "#94A3B8", fontSize: 13, padding: "4px 0" }}>Add options below</Typography>
                          )}
                        </Box>
                        <Box display="flex" style={{ gap: 8 }}>
                          <TextField
                            placeholder="Type an option and press Enter…"
                            value={rule.optionDraft}
                            onChange={(e) => updateInlineRule(rule.id, { optionDraft: e.target.value })}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOptionToInlineRule(rule.id); } }}
                            size="small"
                            variant="outlined"
                            style={{ flex: 1 }}
                          />
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => addOptionToInlineRule(rule.id)}
                            style={{ borderRadius: 8, textTransform: "none" }}
                          >
                            Add
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })}

              {/* ── Eligibility Conditions ── */}
              <Box style={{ marginTop: 20, marginBottom: 20, padding: "16px 18px", background: "#FFFBEB", borderRadius: 12, border: "1px solid #FDE68A" }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 6 }}>
                  <Box>
                    <Typography style={{ fontSize: 13, fontWeight: 700, color: "#92400E", textTransform: "uppercase", letterSpacing: "0.8px" }}>
                      Eligibility Conditions
                    </Typography>
                    <Typography style={{ fontSize: 13, color: "#78350F", marginTop: 2 }}>
                      Conditions that must be TRUE for the beneficiary to be <strong>eligible</strong>. Click <strong>AND</strong>/<strong>OR</strong> between conditions to toggle the logic.
                    </Typography>
                    <Box
                      style={{
                        marginTop: 6,
                        padding: "8px 12px",
                        background: "#FFFBEB",
                        borderRadius: 8,
                        border: "1px solid #FDE68A",
                      }}
                    >
                      <Typography style={{ fontSize: 12, color: "#78350F" }}>
                        <strong>Examples</strong>
                        <br />
                        • Monthly Income <strong>≤ 3000</strong> → only people earning at most 3000 qualify
                        <br />
                        • Household Size <strong>≥ 4</strong> → family must have 4 or more members
                        <br />
                        • Age <strong>≤ 18</strong> → only minors are eligible
                        <br />
                        <em>Tip: to disqualify high earners, add "Monthly Income ≤ 3000" not "income &gt; 3000".</em>
                      </Typography>
                    </Box>
                  </Box>
                  <Box display="flex" flexDirection="column" alignItems="flex-end" style={{ gap: 8 }}>
                    <Box display="flex" style={{ gap: 6 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() => setInlineSection((prev) => ({
                          ...prev,
                          conditions: [...(prev.conditions || []), { id: `cond_${Date.now()}`, field: SOCIAL_CONDITION_FIELDS[0].key, operator: "lte", value: "", connector: "and" }],
                        }))}
                        style={{ borderRadius: 8, textTransform: "none", fontWeight: 600, borderColor: "#F59E0B", color: "#92400E" }}
                      >
                        Add Condition
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() => setInlineSection((prev) => ({
                          ...prev,
                          conditions: [...(prev.conditions || []), { id: `cond_${Date.now()}`, field: "_formula", formula: "", operator: "lt", value: "", connector: "and" }],
                        }))}
                        style={{ borderRadius: 8, textTransform: "none", fontWeight: 600, borderColor: "#6366F1", color: "#4338CA" }}
                      >
                        Add Formula
                      </Button>
                    </Box>
                  </Box>
                </Box>

                {!(inlineSection.conditions || []).length && (
                  <Typography style={{ fontSize: 13, color: "#B45309", fontStyle: "italic" }}>
                    No conditions — this section applies to all beneficiaries regardless of income, age, etc.
                  </Typography>
                )}

                {(inlineSection.conditions || []).map((cond, ci) => {
                  const currentSectionTitle = normalizeSectionTitle(inlineSection.title || inlineSection.originalTitle || "");
                  const allFormSectionGroups = groupedConfiguredFields
                    .map((group) => ({
                      section: group.section,
                      sectionKey: normalizeSectionTitle(group.section),
                      isCurrent: normalizeSectionTitle(group.section) === currentSectionTitle,
                      fields: group.items
                        .filter(({ field }) => field.key && field.label)
                        .map(({ field }) => ({
                          key: `form.${field.key}`,
                          label: field.label,
                          type: field.type || "text",
                          options: parseOptions(field.options || []),
                        })),
                    }))
                    .filter((g) => g.fields.length > 0)
                    .sort((a, b) => (a.isCurrent ? -1 : b.isCurrent ? 1 : 0));

                  // Source options: demographic + one entry per section
                  const sourceOptions = [
                    { key: "_demographic", label: "Demographic", fields: SOCIAL_CONDITION_FIELDS },
                    ...allFormSectionGroups.map((g) => ({
                      key: g.sectionKey,
                      label: g.isCurrent ? `${g.section} (this section)` : g.section,
                      fields: g.fields,
                    })),
                  ];

                  // Derive which source the current field belongs to
                  const currentSourceKey = SOCIAL_CONDITION_FIELDS.find((f) => f.key === cond.field)
                    ? "_demographic"
                    : (allFormSectionGroups.find((g) => g.fields.some((f) => f.key === cond.field))?.sectionKey || "_demographic");
                  const currentSource = sourceOptions.find((s) => s.key === currentSourceKey) || sourceOptions[0];
                  const availableFields = currentSource.fields;

                  const allCondFields = sourceOptions.flatMap((s) => s.fields);
                  const fieldDef = allCondFields.find((f) => f.key === cond.field) || availableFields[0] || null;
                  const fieldType = fieldDef?.type || "number";
                  const allowedOpKeys = OPERATORS_BY_TYPE[fieldType] || CONDITION_OPERATORS.map((o) => o.key);
                  const filteredOps = CONDITION_OPERATORS.filter((o) => allowedOpKeys.includes(o.key));
                  const currentOp = allowedOpKeys.includes(cond.operator) ? cond.operator : allowedOpKeys[0];
                  const choiceOptions = fieldType === "select"
                    ? (fieldDef?.options || [])
                    : fieldType === "boolean"
                    ? ["Yes", "No"]
                    : null;
                  const currentVal = choiceOptions && !choiceOptions.includes(cond.value)
                    ? (choiceOptions[0] || "")
                    : cond.value;
                  const fieldLabel = (fieldDef || { label: cond.field }).label;
                  const opLabel = (CONDITION_OPERATORS.find((o) => o.key === currentOp) || { label: currentOp }).label;
                  const preview = currentVal
                    ? `Eligible if: ${fieldLabel} ${opLabel} ${currentVal}`
                    : null;
                  return (
                    <Box key={cond.id || ci} style={{ marginTop: 10 }}>
                      {ci > 0 && (
                        <Box
                          onClick={() => setInlineSection((prev) => ({
                            ...prev,
                            conditions: prev.conditions.map((c, i) =>
                              i === ci ? { ...c, connector: c.connector === "or" ? "and" : "or" } : c
                            ),
                          }))}
                          style={{ background: cond.connector === "or" ? "#EDE9FE" : "#FEF3C7", borderRadius: 6, padding: "4px 10px", display: "inline-block", marginBottom: 6, cursor: "pointer", userSelect: "none" }}
                          title="Click to toggle AND / OR"
                        >
                          <Typography style={{ fontSize: 12, fontWeight: 800, color: cond.connector === "or" ? "#5B21B6" : "#92400E" }}>
                            {(cond.connector || "and").toUpperCase()}
                          </Typography>
                        </Box>
                      )}
                      {/* Formula condition row */}
                      {cond.field === "_formula" && (() => {
                        // Build variable list: all form field keys across sections
                        const formulaVars = groupedConfiguredFields.flatMap((g) =>
                          g.items
                            .filter(({ field }) => field.key && field.label)
                            .map(({ field }) => ({ key: field.key, label: field.label, section: g.section }))
                        );
                        const insertVar = (varKey) => setInlineSection((prev) => ({
                          ...prev,
                          conditions: prev.conditions.map((c, i) =>
                            i === ci ? { ...c, formula: ((c.formula || "").trimEnd() ? (c.formula || "").trimEnd() + " " : "") + varKey } : c
                          ),
                        }));
                        // Evaluate formula with all variables = 1 to preview grouping/precedence.
                        const previewFormula = (() => {
                          const f = (cond.formula || "").trim();
                          if (!f) return null;
                          // Safety: only allow formula-safe characters
                          if (!/^[A-Za-z0-9_.+\-*/()\s]+$/.test(f)) return { error: "Invalid characters" };
                          try {
                            // Replace each identifier with "1" so the user sees the grouping result
                            const subst = f.replace(/[A-Za-z_][A-Za-z0-9_.]*/g, "1");
                            // eslint-disable-next-line no-new-func
                            const result = new Function("return (" + subst + ")")();
                            if (!isFinite(result)) return { error: "Division by zero or invalid" };
                            return { value: +result.toFixed(4), subst };
                          } catch {
                            return { error: "Syntax error" };
                          }
                        })();
                        return (
                          <Box style={{ width: "100%" }}>
                            <Box display="flex" alignItems="flex-start" style={{ gap: 8, flexWrap: "wrap" }}>
                              <TextField
                                label="Formula"
                                placeholder="e.g. income / (members * 30)"
                                size="small"
                                variant="outlined"
                                value={cond.formula || ""}
                                onChange={(e) => setInlineSection((prev) => ({
                                  ...prev,
                                  conditions: prev.conditions.map((c, i) => i === ci ? { ...c, formula: e.target.value } : c),
                                }))}
                                style={{ flex: "3 1 220px" }}
                                helperText="Click variables below to insert. Use ( ) for grouping — e.g. income / (members * 30)."
                              />
                              {previewFormula && (
                                <Box style={{
                                  alignSelf: "flex-start",
                                  marginTop: 2,
                                  padding: "4px 10px",
                                  borderRadius: 6,
                                  background: previewFormula.error ? "#FEE2E2" : "#D1FAE5",
                                  border: `1px solid ${previewFormula.error ? "#FCA5A5" : "#6EE7B7"}`,
                                  minWidth: 140,
                                }}>
                                  {previewFormula.error ? (
                                    <Typography style={{ fontSize: 11, color: "#B91C1C", fontWeight: 600 }}>
                                      ⚠ {previewFormula.error}
                                    </Typography>
                                  ) : (
                                    <>
                                      <Typography style={{ fontSize: 10, color: "#065F46" }}>
                                        Preview (all vars = 1):
                                      </Typography>
                                      <Typography style={{ fontSize: 12, fontWeight: 700, color: "#065F46", fontFamily: "monospace" }}>
                                        {previewFormula.subst} = {previewFormula.value}
                                      </Typography>
                                    </>
                                  )}
                                </Box>
                              )}
                              <TextField
                                select
                                label="is"
                                size="small"
                                variant="outlined"
                                value={cond.operator || "lt"}
                                onChange={(e) => setInlineSection((prev) => ({
                                  ...prev,
                                  conditions: prev.conditions.map((c, i) => i === ci ? { ...c, operator: e.target.value } : c),
                                }))}
                                style={{ flex: "0 0 110px" }}
                              >
                                {["eq", "ne", "lt", "lte", "gt", "gte"].map((op) => (
                                  <MenuItem key={op} value={op}>{(CONDITION_OPERATORS.find((o) => o.key === op) || { label: op }).label}</MenuItem>
                                ))}
                              </TextField>
                              <TextField
                                label="Value"
                                size="small"
                                variant="outlined"
                                type="number"
                                value={cond.value || ""}
                                onChange={(e) => setInlineSection((prev) => ({
                                  ...prev,
                                  conditions: prev.conditions.map((c, i) => i === ci ? { ...c, value: e.target.value } : c),
                                }))}
                                style={{ flex: "0 0 90px" }}
                              />
                              <IconButton size="small" onClick={() => setInlineSection((prev) => ({ ...prev, conditions: prev.conditions.filter((_, i) => i !== ci) }))}>
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </Box>
                            <Box style={{ marginTop: 6 }}>
                              <Typography style={{ fontSize: 11, color: "#6B7280", marginBottom: 4 }}>
                                Operators &amp; grouping:
                              </Typography>
                              <Box display="flex" flexWrap="wrap" style={{ gap: 4, marginBottom: 8 }}>
                                {["(", ")", "+", "-", "*", "/"].map((sym) => (
                                  <Box
                                    key={sym}
                                    onClick={() => insertVar(sym)}
                                    style={{
                                      cursor: "pointer",
                                      background: "#EFF6FF",
                                      border: "1px solid #93C5FD",
                                      borderRadius: 4,
                                      padding: "2px 10px",
                                      fontWeight: 700,
                                      fontSize: 14,
                                      color: "#1D4ED8",
                                      userSelect: "none",
                                      minWidth: 28,
                                      textAlign: "center",
                                    }}
                                  >
                                    {sym}
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                            {formulaVars.length > 0 && (
                              <Box>
                                <Typography style={{ fontSize: 11, color: "#92400E", marginBottom: 4 }}>
                                  Variables — click to insert:
                                </Typography>
                                <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
                                  {formulaVars.map((v) => (
                                    <Box
                                      key={v.key}
                                      onClick={() => insertVar(v.key)}
                                      title={`${v.label} (from section: ${v.section})\nKey: ${v.key}`}
                                      style={{
                                        cursor: "pointer",
                                        background: "#FEF3C7",
                                        border: "1px solid #F59E0B",
                                        borderRadius: 4,
                                        padding: "2px 8px",
                                        display: "inline-flex",
                                        flexDirection: "column",
                                        alignItems: "flex-start",
                                      }}
                                    >
                                      <Typography style={{ fontSize: 11, fontWeight: 700, color: "#92400E", lineHeight: 1.2 }}>{v.key}</Typography>
                                      <Typography style={{ fontSize: 10, color: "#B45309", lineHeight: 1.2 }}>{v.label}</Typography>
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            )}
                            {formulaVars.length === 0 && (
                              <Typography style={{ fontSize: 11, color: "#B45309", marginTop: 4 }}>
                                No form fields found. Add fields to sections first — their keys become formula variables.
                              </Typography>
                            )}
                          </Box>
                        );
                      })()}
                      {cond.field !== "_formula" && (
                      <>
                      <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: "wrap" }}>
                        {/* Step 1: pick source section */}
                        <TextField
                          select
                          label="From"
                          value={currentSourceKey}
                          onChange={(e) => {
                            const newSrc = sourceOptions.find((s) => s.key === e.target.value) || sourceOptions[0];
                            const firstField = newSrc.fields[0];
                            if (!firstField) return;
                            const newType = firstField.type || "number";
                            const newAllowedOps = OPERATORS_BY_TYPE[newType] || CONDITION_OPERATORS.map((o) => o.key);
                            const newOp = newAllowedOps[0];
                            const newChoices = newType === "select"
                              ? (firstField.options || [])
                              : newType === "boolean" ? ["Yes", "No"] : null;
                            const newVal = newChoices ? (newChoices[0] || "") : "";
                            setInlineSection((prev) => ({
                              ...prev,
                              conditions: prev.conditions.map((c, i) => i === ci
                                ? { ...c, field: firstField.key, operator: newOp, value: newVal }
                                : c),
                            }));
                          }}
                          size="small"
                          variant="outlined"
                          style={{ flex: "2 1 160px" }}
                        >
                          {sourceOptions.map((s) => (
                            <MenuItem key={s.key} value={s.key}>
                              {s.label}
                              <Typography component="span" style={{ fontSize: 11, color: "#94A3B8", marginLeft: 6 }}>
                                ({s.fields.length})
                              </Typography>
                            </MenuItem>
                          ))}
                        </TextField>

                        {/* Step 2: pick field within that source */}
                        <TextField
                          select
                          label="Question"
                          value={availableFields.find((f) => f.key === cond.field) ? cond.field : (availableFields[0]?.key || "")}
                          onChange={(e) => {
                            const newField = e.target.value;
                            const newDef = availableFields.find((f) => f.key === newField);
                            const newType = newDef?.type || "number";
                            const newAllowedOps = OPERATORS_BY_TYPE[newType] || CONDITION_OPERATORS.map((o) => o.key);
                            const newOp = newAllowedOps.includes(cond.operator) ? cond.operator : newAllowedOps[0];
                            const newChoices = newType === "select"
                              ? (newDef?.options || [])
                              : newType === "boolean" ? ["Yes", "No"] : null;
                            const newVal = (newChoices && !newChoices.includes(cond.value)) ? (newChoices[0] || "") : cond.value;
                            setInlineSection((prev) => ({
                              ...prev,
                              conditions: prev.conditions.map((c, i) => i === ci ? { ...c, field: newField, operator: newOp, value: newVal } : c),
                            }));
                          }}
                          size="small"
                          variant="outlined"
                          style={{ flex: "3 1 200px" }}
                        >
                          {availableFields.map((f) => (
                            <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>
                          ))}
                        </TextField>

                        {/* Operator */}
                        <TextField
                          select
                          label="Must be"
                          value={currentOp}
                          onChange={(e) => setInlineSection((prev) => ({
                            ...prev,
                            conditions: prev.conditions.map((c, i) => i === ci ? { ...c, operator: e.target.value } : c),
                          }))}
                          size="small"
                          variant="outlined"
                          style={{ flex: "2 1 130px" }}
                        >
                          {filteredOps.map((op) => (
                            <MenuItem key={op.key} value={op.key}>{op.label}</MenuItem>
                          ))}
                        </TextField>

                        {/* Value */}
                        {choiceOptions ? (
                          <TextField
                            select
                            label="Value"
                            value={currentVal}
                            onChange={(e) => setInlineSection((prev) => ({
                              ...prev,
                              conditions: prev.conditions.map((c, i) => i === ci ? { ...c, value: e.target.value } : c),
                            }))}
                            size="small"
                            variant="outlined"
                            style={{ flex: "2 1 140px" }}
                          >
                            {choiceOptions.map((opt) => (
                              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                          </TextField>
                        ) : (
                          <TextField
                            label="Value"
                            value={cond.value}
                            onChange={(e) => setInlineSection((prev) => ({
                              ...prev,
                              conditions: prev.conditions.map((c, i) => i === ci ? { ...c, value: e.target.value } : c),
                            }))}
                            size="small"
                            variant="outlined"
                            placeholder="e.g. 3000"
                            style={{ flex: "2 1 110px" }}
                          />
                        )}
                        <IconButton
                          size="small"
                          onClick={() => setInlineSection((prev) => ({
                            ...prev,
                            conditions: prev.conditions.filter((_, i) => i !== ci),
                          }))}
                          style={{ color: "#EF4444" }}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Box>
                      {preview && (
                        <Box style={{ marginTop: 4, padding: "4px 10px", background: "#F0FDF4", borderRadius: 6, border: "1px solid #BBF7D0", display: "inline-block" }}>
                          <Typography style={{ fontSize: 12, color: "#15803D" }}>✓ {preview}</Typography>
                        </Box>
                      )}
                      </>
                      )}
                    </Box>
                  );
                })}
              </Box>

              {/* ── Actions ── */}
              <Box display="flex" alignItems="center" style={{ gap: 12, paddingTop: 4 }}>
                <Button
                  onClick={closeInlineSection}
                  style={{ borderRadius: 10, textTransform: "none", fontWeight: 600, color: "#64748B" }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!inlineSection.title.trim() || !inlineSection.rules.filter((r) => r.label.trim()).length}
                  onClick={commitInlineSection}
                  style={{ borderRadius: 10, textTransform: "none", fontWeight: 700, fontSize: 15, padding: "10px 28px" }}
                >
                  {inlineSection.originalTitle ? "Update Section" : "Save Section"}
                </Button>
              </Box>
            </Box>
          </Paper>
        ) : (
          groupedConfiguredFields.length > 0 && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Add />}
              onClick={() => openInlineSection(null)}
              style={{ borderRadius: 12, textTransform: "none", fontWeight: 700, fontSize: 16, padding: "12px 32px", borderWidth: 2, marginTop: 8 }}
            >
              Add Another Section
            </Button>
          )
        )}
      </Box>
    </Box>
  );
};


const ITEM_TYPE_OPTIONS = [
  { value: "D", label: "Drug" },
  { value: "M", label: "Consumable" },
  { value: "O", label: "Other" },
];

const ItemTypePickerWithOther = ({ value, onChange, withNull, required, readOnly, nullLabel }) => (
  <TextField
    select
    label="Item Type"
    value={value ?? ""}
    onChange={(e) => onChange && onChange(e.target.value || null)}
    required={required}
    disabled={readOnly}
    size="small"
    fullWidth
  >
    {withNull && <MenuItem value="">{nullLabel || "Any"}</MenuItem>}
    {ITEM_TYPE_OPTIONS.map((opt) => (
      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
    ))}
  </TextField>
);

export function CustomAdminMenuModule() {
  const eligibilityEntry = {
    text: "HF Eligibility Rules UI Builder",
    icon: <Tune />,
    route: "/hf/eligibility",
    filter: (rights = []) => rights.includes(RIGHT_BENEFIT_PLAN_SEARCH),
    id: "socialProtection.eligibilityRules",
  };

  const socialRulesEntry = {
    text: "Social Rules UI Builder",
    icon: <Tune />,
    route: "/hf/social-ui-builder",
    filter: (rights = []) => rights.includes(RIGHT_BENEFIT_PLAN_SEARCH),
    id: "socialProtection.socialRules",
  };

  const translationEntry = {
    text: "Translations",
    icon: <Tune />,
    route: "/hf/translations",
    filter: (rights = []) => rights.includes(RIGHT_BENEFIT_PLAN_SEARCH),
    id: "socialProtection.translationAdmin",
  };

  const addonRulesEntry = {
    text: "ADDON Rules",
    icon: <Tune />,
    route: "/hf/addon-rules",
    filter: (rights = []) => rights.includes(RIGHT_BENEFIT_PLAN_SEARCH),
    id: "socialProtection.addonRules",
  };

  const beneficiaryDetailsEntry = {
    text: "Beneficiary Details",
    icon: <Tune />,
    route: "/hf/beneficiary-details",
    filter: (rights = []) => rights.includes(RIGHT_BENEFIT_PLAN_SEARCH),
    id: "socialProtection.beneficiaryDetails",
  };

  return {
    "admin.MainMenu": [eligibilityEntry, socialRulesEntry, translationEntry, addonRulesEntry, beneficiaryDetailsEntry],
    refs: [{ key: "medical.ItemTypePicker", ref: ItemTypePickerWithOther }],
    "core.Router": [
      {
        path: "hf/eligibility",
        component: EligibilityRulesManagerPage,
      },
      {
        path: "hf/social-ui-builder",
        component: SocialUiBuilderPage,
      },
      {
        path: "hf/translations",
        component: TranslationAdminPage,
      },
      {
        path: "hf/addon-rules",
        component: AddonRulesManagerPage,
      },
      {
        path: "hf/beneficiary-details",
        component: BeneficiaryInsureeDetailsPage,
      },
    ],
  };
}

export default CustomAdminMenuModule;
